import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { forgetFileBlob, rememberFileBlob } from '../data/file-blobs';
import {
  DEFAULT_MODEL_ID,
  isDefaultModel,
  isSessionModelUnknown,
  type ModelName,
} from '../data/models';
import type { ThinkingChoice } from '../data/thinking-levels';
import { withSources } from '../health';
import { tr } from '../i18n';
import { precedingUserFiles, vaultSaveFiles } from '../proposal-files';
import { getChatService } from '../service/ai-cio-service';
import { normalizeOutputName, type CompactAnswer } from '../service/orchestrator-client';
import { isChatOpenInOtherTab } from '../tab-binding';
import { CONNECT_CARD_SCHEMA } from '../types';
import type {
  AttachedFile,
  AttachedRef,
  ChatSystemNote,
  CompactionOutcome,
  ConnectCardRecord,
  ContentBlock,
  ContextFigure,
  Conversation,
  DeleteOutcome,
  InteractionAnswer,
  InteractionProposal,
  InteractionQuestion,
  LiveSession,
  Message,
  MessageFeedback,
  PendingResultFile,
  ProcessingStep,
  ProposalDecision,
  ProposalDecisionKind,
  RanWith,
  SendOptions,
  StreamChunk,
  TranscriptTurn,
  TurnActivity,
  TurnAnswer,
  VaultSave,
} from '../types';

import { NEW_CHAT_DRAFT_KEY, useComposerAttachmentsStore } from './composer-attachments.store';
import { useThinkingLevelStore } from './thinking-level.store';
import { userScopedLocalStorage } from './user-scoped-storage';

// ── identity-scoped persistence (DEV-3506) ──────────────────────────────────
// AI CIO conversations must never surface for a DIFFERENT user on a shared
// browser. Persist under a key suffixed with the logged-in user id (read
// synchronously from the `auth-user` localStorage entry), so a different login
// reads a different bucket even without a logout in between. Logout also wipes
// every `investbanq-ai-cio*` key (see shared/auth `logout`). The old unscoped
// `investbanq-ai-cio` key is never read again.
const AI_CIO_STORAGE_KEY = 'investbanq-ai-cio';

// ── id helper (mirrors tabs.store convention) ───────────────────────────────

let _seq = 0;
function uid(prefix: string): string {
  return `${prefix}${Date.now().toString()}-${(++_seq).toString()}`;
}

function proposalDecisionEcho(
  proposal: InteractionProposal,
  decision: ProposalDecisionKind,
  relatedFiles: AttachedFile[] | undefined,
): string {
  if (proposal.schema === 'vault_save_v1' && vaultSaveFiles(proposal, relatedFiles).length > 0) {
    return decision === 'approve'
      ? tr('vaultSave.approve', 'Save to Vault')
      : tr('vaultSave.reject', 'Don’t save');
  }
  const isMemory = proposal.schema === 'remember_preference_v1';
  if (decision === 'approve')
    return isMemory ? tr('proposal.save', 'Save to AI Memory') : tr('proposal.approve', 'Approve');
  return isMemory ? tr('proposal.dismiss', "Don't save") : tr('proposal.reject', 'Reject');
}

function deriveTitle(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (!trimmed) return tr('history.new', 'New chat');
  return trimmed.length > 40 ? `${trimmed.slice(0, 40)}…` : trimmed;
}

// ── State ────────────────────────────────────────────────────────────────────

interface ConversationsState {
  conversations: Record<string, Conversation>;
  /** Insertion/order list, most-recent first. */
  order: string[];
  activeId: string | null;
  /** Per-session in-flight aborts, keyed by conversation id (not persisted). */

  // selectors-as-actions
  getActive: () => Conversation | null;
  /** True when the active conversation is a fresh chat with no messages yet. */
  isActiveEmpty: () => boolean;

  newChat: (modelId?: string, opts?: { forceNew?: boolean }) => string;
  selectConversation: (id: string) => void;
  /** Sets the active chat's model (a new chat first, with none), with its name when given. */
  setModel: (modelId: string, name?: ModelName) => void;
  /** Sets the active chat's thinking choice (a new chat first, with none), and the user's last. */
  setThinking: (choice: ThinkingChoice) => void;
  /** Sets the active chat's Agentic switch (a new chat first, with none). */
  setAgentic: (on: boolean) => void;
  setFeedback: (messageId: string, feedback: MessageFeedback) => void;
  /**
   * Sends a user message in the active conversation, streaming the reply. While a reply is
   * being written there, the message is queued behind it instead.
   */
  sendMessage: (text: string, files?: AttachedFile[]) => Promise<void>;
  /**
   * Re-runs the assistant reply for `messageId`, in place, regardless of its position in the
   * thread. Does nothing while a reply is being written in the conversation.
   */
  regenerate: (messageId: string, opts?: { forceInternal?: boolean }) => Promise<void>;
  appendSystemNote: (id: string, note: ChatSystemNote) => void;
  /** Answers an open MCQ/interaction question and streams the continued turn. */
  answerInteraction: (answer: InteractionAnswer) => Promise<void>;
  decideProposal: (decision: ProposalDecision) => Promise<void>;
  /**
   * Answers a Connect card with `approve` in its own conversation, active or not, with no echo:
   * the search runs and its continuation streams into a new reply after the card's message, on
   * the model the conversation had when the card arrived. The card becomes a record. Nothing for
   * a card that is a record already (CC D15).
   */
  answerConnectCard: (conversationId: string, proposal: InteractionProposal) => Promise<void>;
  /** Sets a Connect card's pending Connect, outcome or badge; nothing once it is a record. */
  updateConnectCard: (
    conversationId: string,
    proposalId: string,
    patch: Partial<Pick<ConnectCardRecord, 'pending' | 'outcome' | 'badge'>>,
  ) => void;
  /** Renames a conversation (and mirrors to the backend index). */
  renameConversation: (id: string, title: string) => void;
  /**
   * Deletes a conversation: every session it was bound to on the orchestrator first,
   * then its backend index rows and the local copy. On 'busy' or 'failed' nothing is
   * removed. A draft that never reached the server is removed locally.
   */
  deleteConversation: (id: string) => Promise<DeleteOutcome>;
  /**
   * Clears all history: aborts every stream, clears the orchestrator, then the backend
   * index, then resets the store to one fresh chat. On 'busy' or 'failed' nothing local
   * is removed; both server calls are idempotent, so a retry is safe.
   */
  clearHistory: () => Promise<DeleteOutcome>;
  /**
   * Reconciles the local list with a successful fetch of the server-side history
   * index (an empty one included). Conversations known to the server but not held
   * locally appear as lazy stubs (messages are fetched from the orchestrator when
   * opened). A synced conversation the index no longer lists was deleted or cleared
   * elsewhere and is dropped, unless its session was bound in this page load.
   */
  hydrateFromHistory: (
    items: { sessionId: string; title: string; createdAt: number; updatedAt: number }[],
  ) => void;
  /** Transcript restore state for server-known stubs, by conversation id (not persisted). */
  historyStatus: Record<string, 'loading' | 'error'>;
  /**
   * Fetches a server-known conversation's transcript (the archive, else the live session) when
   * it has no messages loaded yet. No-op for local chats, loaded ones, or while already loading.
   */
  loadTranscript: (id: string) => Promise<void>;
  /** What each conversation's reply is doing, by conversation id (not persisted). */
  activity: Record<string, TurnActivity>;
  /**
   * Stops the reply being written in a conversation at its next safe point. Its stream goes
   * on to the stop card, which asks whether to give what it has so far, or, when nothing had
   * finished, to one line that says so.
   */
  stopTurn: (id: string) => Promise<void>;
  /** Removes a queued message: true when it was removed before a turn took it. */
  removeQueued: (id: string, messageId: string) => Promise<boolean>;
  /** Takes every queued message back to edit, removed first: their texts, a paragraph each. */
  takeBackQueued: (id: string) => Promise<string>;
  /**
   * "Send now" for queued messages that wait for the user: a load found them too old to run
   * on their own, or a stop that raised no card holds them. A message the session would not
   * queue goes first, as a turn that takes what the session holds before it.
   */
  sendQueuedNow: (id: string) => void;
  /**
   * Reads a conversation's live session, once per page load: a reply still being written is
   * followed until it ends, the open stop card shows, and the queue is reconciled and run,
   * unless a stop holds it.
   */
  syncSession: (id: string) => Promise<void>;
  /**
   * Compacts a conversation (DEV-4598): its session folds older turns into a summary the model
   * reads in their place. One press at a time, never while the session is busy; it counts as
   * busy until it ends, and its outcome shows on the composer.
   */
  compact: (id: string) => Promise<void>;
  /** Takes the outcome of the conversation's last press off the composer. */
  dismissCompactionNote: (id: string) => void;
}

export function conversationById(
  conversations: Record<string, Conversation>,
  id: string,
): Conversation | undefined {
  return Object.hasOwn(conversations, id) ? conversations[id] : undefined;
}

/**
 * A conversation that has never been used: no local messages AND not known to the server.
 * Server-known conversations hydrate as message-less stubs (sessionId set) — those are real
 * history entries, never "blank".
 */
export function isBlankConversation(c: Conversation): boolean {
  return Array.isArray(c.messages) && c.messages.length === 0 && c.sessionId === undefined;
}

function strandedDraftKey(active: Conversation | null): string | undefined {
  if (!active) return NEW_CHAT_DRAFT_KEY;
  if (!isBlankConversation(active) || isChatOpenInOtherTab(active.id)) return undefined;
  return active.id;
}

function handOverDraft(fromKey: string | undefined, toKey: string): void {
  if (fromKey === undefined || fromKey === toKey) return;
  const composerDrafts = useComposerAttachmentsStore.getState();
  composerDrafts.move(fromKey, toKey);
  composerDrafts.clear(fromKey);
}

function forgetChats(chats: readonly Conversation[]): void {
  const composerDrafts = useComposerAttachmentsStore.getState();
  for (const chat of chats) {
    composerDrafts.clear(chat.id);
    for (const m of chat.messages) heldFiles.delete(m.id);
  }
  forgetFileBlob(
    chats.flatMap((chat) => chat.messages.flatMap((m) => m.files?.map((file) => file.id) ?? [])),
  );
}

/**
 * The one reading of a chat's thinking choice: its own, else the user's last choice, which a
 * chat kept before levels were per chat reads until its first write.
 */
export function chatChoice(conv: Pick<Conversation, 'thinking'>): ThinkingChoice | null {
  return conv.thinking !== undefined ? conv.thinking : useThinkingLevelStore.getState().choice;
}

// Deletes are mirrored to the server fire-and-forget; remember them for this page session so
// a history refresh that lands before the server applied the delete can't resurrect the row.
const locallyDeletedSessions = new Set<string>();

// Sessions bound in this page load: their index rows may not be written yet, so a history
// refresh that does not list them never drops their conversations.
const boundSessions = new Set<string>();

// In-flight requests per conversation id: a delete, a clear or a dropped stub aborts them all.
const abortControllers = new Map<string, Set<AbortController>>();

// Conversations whose live session was read in this page load (see `syncSession`).
const syncedConversations = new Set<string>();

// ── CRM history-index hooks ──────────────────────────────────────────────────
// The store stays free of the GraphQL client; the app injects these so a new
// or renamed conversation is mirrored into the backend `aiCioConversations`
// index. No-ops until wired (e.g. in tests). `onSessionBound` and
// `onHistoryCleared` resolve true when the backend accepted the change.

let onSessionBound: ((sessionId: string, title: string) => Promise<boolean>) | undefined;
let onTitleChanged: ((sessionId: string, title: string) => void) | undefined;
let onConversationDeleted: ((sessionId: string) => void) | undefined;
let onHistoryCleared: (() => Promise<boolean>) | undefined;
let onTurnFinished: ((conversationId: string) => void) | undefined;

export function setTurnHooks(hooks: { onTurnFinished?: (conversationId: string) => void }): void {
  onTurnFinished = hooks.onTurnFinished;
}

export function setHistorySync(hooks: {
  onSessionBound?: (sessionId: string, title: string) => Promise<boolean>;
  onTitleChanged?: (sessionId: string, title: string) => void;
  onConversationDeleted?: (sessionId: string) => void;
  onHistoryCleared?: () => Promise<boolean>;
}): void {
  onSessionBound = hooks.onSessionBound;
  onTitleChanged = hooks.onTitleChanged;
  onConversationDeleted = hooks.onConversationDeleted;
  onHistoryCleared = hooks.onHistoryCleared;
}

export const useConversationsStore = create<ConversationsState>()(
  persist(
    (set, get) => ({
      conversations: {},
      order: [],
      activeId: null,

      getActive() {
        const { activeId, conversations } = get();
        return activeId ? (conversationById(conversations, activeId) ?? null) : null;
      },

      isActiveEmpty() {
        const active = get().getActive();
        return !active || isBlankConversation(active);
      },

      newChat(modelId, opts) {
        const active = get().getActive();
        // A new chat starts at the user's last thinking choice, with Agentic off.
        const thinking = useThinkingLevelStore.getState().choice;
        // Reuse an existing empty chat unless the caller forces a fresh one; it starts as a
        // created one does.
        if (!opts?.forceNew && active && isBlankConversation(active)) {
          set((s) => ({
            conversations: {
              ...s.conversations,
              [active.id]: { ...active, agentic: undefined, thinking },
            },
            activeId: active.id,
          }));
          return active.id;
        }

        const draftKey = strandedDraftKey(active);
        const id = uid('c');
        const now = Date.now();
        const chatModel = modelId ?? active?.modelId ?? DEFAULT_MODEL_ID;
        const conversation: Conversation = {
          id,
          title: tr('history.new', 'New chat'),
          messages: [],
          modelId: chatModel,
          // The model's name goes with it.
          modelName: active && chatModel === active.modelId ? active.modelName : undefined,
          thinking,
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({
          conversations: { ...s.conversations, [id]: conversation },
          order: [id, ...s.order],
          activeId: id,
        }));
        handOverDraft(draftKey, id);
        return id;
      },

      selectConversation(id) {
        if (!conversationById(get().conversations, id)) return;
        const draftKey = strandedDraftKey(get().getActive());
        set({ activeId: id });
        handOverDraft(draftKey, id);
      },

      setModel(modelId, name) {
        if (!get().getActive()) get().newChat(modelId);
        const active = get().getActive();
        if (!active) return;
        const modelName = isDefaultModel(modelId) ? undefined : name;
        set((s) => ({
          conversations: {
            ...s.conversations,
            [active.id]: { ...active, modelId, modelName },
          },
        }));
      },

      setThinking(choice) {
        const value = { id: choice.id, rank: choice.rank };
        useThinkingLevelStore.getState().setChoice(value);
        if (!get().getActive()) get().newChat();
        const active = get().getActive();
        if (!active) return;
        set((s) => ({
          conversations: { ...s.conversations, [active.id]: { ...active, thinking: value } },
        }));
      },

      setAgentic(on) {
        if (!get().getActive()) get().newChat();
        const active = get().getActive();
        if (!active) return;
        set((s) => ({
          conversations: { ...s.conversations, [active.id]: { ...active, agentic: on } },
        }));
      },

      setFeedback(messageId, feedback) {
        const active = get().getActive();
        if (!active) return;
        set((s) => ({
          conversations: {
            ...s.conversations,
            [active.id]: {
              ...active,
              messages: active.messages.map((m) =>
                m.id === messageId
                  ? { ...m, feedback: m.feedback === feedback ? null : feedback }
                  : m,
              ),
            },
          },
        }));
      },

      async sendMessage(text, files) {
        const trimmed = text.trim();
        const attachments = files?.filter((f) => Boolean(f.file ?? f.fileRef)) ?? [];
        if (!trimmed && attachments.length === 0) return;

        // Ensure there is an active conversation.
        let id = get().activeId;
        if (!id || !conversationById(get().conversations, id)) {
          id = get().newChat();
        }
        const conversationId = id;
        // The chat's next Send takes the outcome of its last press off the composer.
        const press = runtimes.get(conversationId);
        if (press?.outcome) {
          press.outcome = null;
          publish(set, conversationId);
        }

        const fileMeta: AttachedFile[] | undefined = attachments.length
          ? attachments.map(({ file: _file, ...meta }) => meta)
          : undefined;
        for (const attachment of attachments) {
          if (attachment.file) rememberFileBlob(attachment.id, attachment.file);
        }

        // A message sent while a reply is being written waits behind it in the session's
        // queue. Either way it moves on from an open stop, mode or offer card.
        const queued = isBusy(conversationId);
        retireMovedOnCards(set, get, conversationId);

        const now = Date.now();
        // Its id is also the one the session's queue knows it by.
        const userMessage: Message = {
          id: uid('m'),
          role: 'user',
          content: trimmed,
          createdAt: now,
          status: queued ? 'queued' : 'complete',
          queueState: queued ? 'sending' : undefined,
          files: fileMeta,
        };
        const assistantMessage: Message = {
          id: uid('m'),
          role: 'assistant',
          content: '',
          createdAt: now + 1,
          status: 'streaming',
        };

        set((s) => {
          const conv = s.conversations[conversationId];
          if (!conv) return s;
          const isFirst = isBlankConversation(conv);
          const titleSeed = trimmed || attachments[0]?.name || '';
          return {
            conversations: {
              ...s.conversations,
              [conversationId]: {
                ...conv,
                title: isFirst && titleSeed ? deriveTitle(titleSeed) : conv.title,
                // A queued message waits last; a sent one and its reply go before the queue.
                messages: queued
                  ? [...conv.messages, userMessage]
                  : beforeQueued(conv.messages, [userMessage, assistantMessage]),
                updatedAt: now,
              },
            },
            // Bump to front of recents.
            order: [conversationId, ...s.order.filter((x) => x !== conversationId)],
          };
        });

        if (queued) {
          await sendQueued(set, get, conversationId, userMessage, attachments);
          return;
        }
        await sendIdle(set, get, conversationId, userMessage, assistantMessage.id, attachments);
      },

      async regenerate(messageId, opts) {
        const active = get().getActive();
        if (!active) return;
        // One reply at a time: a reply being written is never replaced by a regenerate.
        if (isBusy(active.id)) return;
        const targetIndex = active.messages.findIndex((m) => m.id === messageId);
        if (targetIndex === -1) return;

        // Walk back from the clicked message to the user turn it replied to,
        // regardless of how many messages follow it in the thread.
        let userIndex = targetIndex - 1;
        while (userIndex >= 0 && active.messages[userIndex]?.role !== 'user') userIndex--;
        if (userIndex < 0) return;
        const lastUser = active.messages[userIndex];
        if (!lastUser) return;

        // Everything before the prompt being re-answered — later turns are left
        // untouched in the UI but excluded from context, since they didn't
        // exist yet at this point in the conversation.
        const historyMessages = active.messages.slice(0, userIndex + 1);

        set((s) => {
          const conv = s.conversations[active.id];
          if (!conv) return s;
          return {
            conversations: {
              ...s.conversations,
              [active.id]: {
                ...conv,
                messages: conv.messages.map((m) =>
                  m.id === messageId
                    ? {
                        ...m,
                        content: '',
                        status: 'streaming',
                        feedback: null,
                        blocks: undefined,
                        blockOrder: undefined,
                        health: undefined,
                        sources: undefined,
                        pendingInteraction: undefined,
                        pendingProposal: undefined,
                        pendingFiles: undefined,
                        connectCards: m.connectCards?.map(asRecord),
                        modeNote: undefined,
                        ran: undefined,
                        steps: undefined,
                        waiting: undefined,
                        route: undefined,
                        workedForMs: undefined,
                        failure: undefined,
                      }
                    : m,
                ),
                updatedAt: Date.now(),
              },
            },
          };
        });

        await streamInto(
          set,
          get,
          active.id,
          { target: messageId, history: historyMessages, resent: lastUser.id },
          (streamOpts) =>
            getChatService().streamReply(lastUser.content, {
              ...streamOpts,
              files: lastUser.files?.map(({ fileRef: _fileRef, ...meta }) => meta),
              forceInternal: opts?.forceInternal,
            }),
        );
      },

      appendSystemNote(id, note) {
        set((s) => {
          const conv = s.conversations[id];
          if (!conv) return s;
          return {
            conversations: {
              ...s.conversations,
              [id]: { ...conv, systemNotes: [...(conv.systemNotes ?? []), note] },
            },
          };
        });
      },

      async answerInteraction(answer) {
        const active = get().getActive();
        if (!active) return;
        const service = getChatService();
        if (!service.answerInteraction) return;
        const answerFn = service.answerInteraction.bind(service);

        // The user's choice is echoed as their message, and the continued turn
        // streams into a fresh reply after it.
        const echo =
          answer.text?.trim() ||
          answer.question.options.find((o) => o.id === answer.selection)?.label ||
          answer.selection;
        // The chat's choice and switch as the user answers: a stop card's "Something else"
        // runs with them, also when it waits for a running turn to end.
        const answered: InteractionAnswer = {
          ...answer,
          controls: { choice: chatChoice(active), agentic: active.agentic === true },
        };
        // A typed phrase's mode card shows on the empty reply of the message it holds: its
        // answer runs that message, and streams into that reply, with no echo. The offer of a
        // deeper pass, a mode card on a reply that already holds an answer, is answered as any
        // other card is.
        const holder = active.messages.find(
          (m) => m.pendingInteraction?.questionId === answer.question.questionId,
        );
        const inPlace =
          answer.question.flow === MODE_CARD_FLOW &&
          holder !== undefined &&
          !holder.content &&
          !holder.blocks?.length
            ? holder.id
            : undefined;
        await answerCard(
          set,
          get,
          active.id,
          {
            id: answer.question.questionId,
            stop: answer.question.flow === STOP_CARD_FLOW,
            echo,
            echoOf: 'interaction',
            inPlace,
          },
          (opts) => answerFn(answered, opts),
        );
      },

      async decideProposal({ proposal, decision }) {
        const active = get().getActive();
        if (!active) return;
        const service = getChatService();
        if (!service.decideProposal) return;
        const decideFn = service.decideProposal.bind(service);

        const proposalIndex = active.messages.findIndex(
          (m) => m.pendingProposal?.proposalId === proposal.proposalId,
        );
        const relatedFiles =
          proposalIndex === -1 ? undefined : precedingUserFiles(active.messages, proposalIndex);

        await answerCard(
          set,
          get,
          active.id,
          {
            id: proposal.proposalId,
            stop: false,
            echo: proposalDecisionEcho(proposal, decision, relatedFiles),
            echoOf: 'proposal',
          },
          (opts) => decideFn({ proposal, decision }, opts),
        );
      },

      async answerConnectCard(conversationId, proposal) {
        const card = findConnectCard(get, conversationId, proposal.proposalId);
        const service = getChatService();
        if (!card || card.record || !service.decideProposal) return;
        const decideFn = service.decideProposal.bind(service);
        // It answers once: from here it is the Connected record, whatever the answer meets.
        patchConnectCard(set, conversationId, proposal.proposalId, (c) => ({
          ...c,
          record: true,
          badge: 'connected',
          pending: undefined,
          outcome: undefined,
        }));
        await answerCard(
          set,
          get,
          conversationId,
          { id: proposal.proposalId, stop: false, modelId: card.modelId },
          (opts) => decideFn({ proposal, decision: 'approve' }, opts),
        );
      },

      updateConnectCard(conversationId, proposalId, patch) {
        patchConnectCard(set, conversationId, proposalId, (c) =>
          c.record ? c : { ...c, ...patch },
        );
      },

      renameConversation(id, title) {
        const next = title.trim();
        if (!next) return;
        const conv = conversationById(get().conversations, id);
        if (!conv) return;
        set((s) => ({
          conversations: { ...s.conversations, [id]: { ...conv, title: next } },
        }));
        if (conv.sessionId) onTitleChanged?.(conv.sessionId, next);
      },

      async deleteConversation(id) {
        const conv = conversationById(get().conversations, id);
        if (!conv) return 'deleted';
        abortConversation(set, id);
        const sessionIds = [
          ...(conv.priorSessionIds ?? []),
          ...(conv.sessionId ? [conv.sessionId] : []),
        ];
        // The orchestrator first: if the index row and the local copy went first and this
        // failed, the transcript would outlive a chat the user saw deleted.
        for (const sessionId of sessionIds) {
          const outcome = await getChatService().deleteSession(sessionId);
          if (outcome !== 'deleted') return outcome;
        }
        // A history stub bound to one of these sessions (the row of a session this chat
        // was rebound from) is part of the local copy too.
        const deleted = new Set(sessionIds);
        const removed = new Set(
          Object.values(get().conversations)
            .filter((c) => c.id === id || (c.sessionId !== undefined && deleted.has(c.sessionId)))
            .map((c) => c.id),
        );
        for (const key of removed) abortConversation(set, key);
        forgetChats(Object.values(get().conversations).filter((c) => removed.has(c.id)));
        set((s) => {
          const rest = Object.fromEntries(
            Object.entries(s.conversations).filter(([key]) => !removed.has(key)),
          );
          const order = s.order.filter((x) => !removed.has(x));
          return {
            conversations: rest,
            order,
            activeId:
              s.activeId !== null && removed.has(s.activeId) ? (order[0] ?? null) : s.activeId,
          };
        });
        for (const sessionId of sessionIds) {
          locallyDeletedSessions.add(sessionId);
          onConversationDeleted?.(sessionId);
        }
        return 'deleted';
      },

      async clearHistory() {
        // The sessions a reply is being written in: a refused clear stops them before retrying.
        const running = Object.values(get().conversations).flatMap((c) =>
          isBusy(c.id) && c.sessionId ? [c.sessionId] : [],
        );
        for (const id of [...abortControllers.keys(), ...runtimes.keys()]) {
          abortConversation(set, id);
        }
        const outcome = await getChatService().clearHistory(running);
        if (outcome !== 'deleted') return outcome;
        if (onHistoryCleared && !(await onHistoryCleared())) return 'failed';
        forgetChats(Object.values(get().conversations));
        set({ conversations: {}, order: [], activeId: null, historyStatus: {}, activity: {} });
        get().newChat(undefined, { forceNew: true });
        return 'deleted';
      },

      historyStatus: {},

      async loadTranscript(id) {
        const conv = conversationById(get().conversations, id);
        const service = getChatService();
        if (!conv?.sessionId || conv.messages.length > 0 || !service.loadTranscript) return;
        if (get().historyStatus[id] === 'loading') return;
        const sessionId = conv.sessionId;
        set((s) => ({ historyStatus: { ...s.historyStatus, [id]: 'loading' } }));
        const clearStatus = (status?: 'error') =>
          set((s) => {
            const rest = Object.fromEntries(
              Object.entries(s.historyStatus).filter(([key]) => key !== id),
            );
            return { historyStatus: status ? { ...rest, [id]: status } : rest };
          });
        try {
          const turns = await service.loadTranscript(sessionId);
          // Nothing renderable (e.g. the first stream failed before a turn was saved):
          // show the "couldn't load" line instead of an endless "Loading…".
          if (turns.length === 0) {
            clearStatus('error');
            return;
          }
          set((s) => {
            const current = s.conversations[id];
            if (!current) return s; // deleted meanwhile
            const restored = restoreTurns(sessionId, turns, current.createdAt);
            // If the user already typed into it while loading, keep their messages after
            // the restored history rather than dropping the history.
            const messages = [...restored, ...current.messages];
            return { conversations: { ...s.conversations, [id]: { ...current, messages } } };
          });
          clearStatus();
        } catch {
          clearStatus('error');
        }
      },

      hydrateFromHistory(items) {
        const listed = new Set(items.map((item) => item.sessionId));
        const dropped = new Set(
          Object.values(get().conversations)
            .filter(
              (c) =>
                c.synced === true &&
                c.sessionId !== undefined &&
                !listed.has(c.sessionId) &&
                !boundSessions.has(c.sessionId),
            )
            .map((c) => c.id),
        );
        for (const id of dropped) abortConversation(set, id);
        forgetChats(Object.values(get().conversations).filter((c) => dropped.has(c.id)));
        set((s) => {
          const conversations = Object.fromEntries(
            Object.entries(s.conversations).filter(([key]) => !dropped.has(key)),
          );
          const order = s.order.filter((x) => !dropped.has(x));
          // Index local conversations by their bound sessionId.
          const bySession = new Map<string, string>();
          for (const c of Object.values(conversations)) {
            if (c.sessionId) bySession.set(c.sessionId, c.id);
          }
          for (const item of items) {
            if (locallyDeletedSessions.has(item.sessionId)) continue;
            const localId = bySession.get(item.sessionId);
            if (localId) {
              const existing = conversations[localId];
              if (existing) {
                conversations[localId] = { ...existing, title: item.title, synced: true };
              }
              continue;
            }
            // Lazy stub — transcript is fetched from the orchestrator on open.
            const id = uid('c');
            conversations[id] = {
              id,
              title: item.title,
              messages: [],
              modelId: DEFAULT_MODEL_ID,
              sessionId: item.sessionId,
              synced: true,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
            };
            order.push(id);
          }
          // Most-recent first.
          order.sort(
            (a, b) => (conversations[b]?.updatedAt ?? 0) - (conversations[a]?.updatedAt ?? 0),
          );
          const activeId =
            s.activeId !== null && dropped.has(s.activeId) ? (order[0] ?? null) : s.activeId;
          return { conversations, order, activeId };
        });
      },

      activity: {},

      async stopTurn(id) {
        const conv = get().conversations[id];
        const service = getChatService();
        const r = runtimes.get(id);
        if (!conv || !service.stopTurn || !r) return;
        if ((r.streams === 0 && !r.following) || r.stopping || r.stopInFlight) return;
        if (!conv.sessionId || r.preflight) {
          // The first send is still creating the session: the stop goes once its turn starts.
          r.stopping = true;
          r.stopWhenBound = true;
          publish(set, id);
          return;
        }
        const sessionId = conv.sessionId;
        r.stopping = true;
        r.stopInFlight = true;
        publish(set, id);
        let stopping = false;
        try {
          stopping = await service.stopTurn(sessionId);
        } catch {
          // The stop was not asked for: the reply goes on, and Stop can be pressed again.
        }
        r.stopInFlight = false;
        // No turn held the session: it has just ended.
        if (!stopping) r.stopping = false;
        if (runtimes.get(id) !== r) return; // deleted meanwhile
        if (r.drainAfterStop) {
          r.drainAfterStop = false;
          void drain(set, get, id);
        }
        publish(set, id);
      },

      async removeQueued(id, messageId) {
        const conv = get().conversations[id];
        const row = conv?.messages.find((m) => m.id === messageId && isQueuedRow(m));
        if (!conv || !row || row.queueState === 'sending') return false;
        if (row.queueState === undefined || row.queueState === 'stale') {
          // The session holds it: it is removed there first, unless a turn has taken it.
          const service = getChatService();
          if (!conv.sessionId || !service.removeQueued) return false;
          let removed: boolean;
          try {
            removed = await service.removeQueued(conv.sessionId, messageId);
          } catch {
            return false;
          }
          // Not removed: a turn took it, and its reply shows it.
          removeQueuedRows(set, id, [messageId]);
          return removed;
        }
        removeQueuedRows(set, id, [messageId]);
        return true;
      },

      async takeBackQueued(id) {
        const texts: string[] = [];
        for (const row of queuedRows(get, id).filter((m) => m.queueState !== 'sending')) {
          if (await get().removeQueued(id, row.id)) texts.push(row.content);
        }
        return texts.join('\n\n');
      },

      sendQueuedNow(id) {
        patchMessages(set, id, (messages) =>
          messages.map((m) =>
            isQueuedRow(m) && m.queueState === 'stale' ? { ...m, queueState: undefined } : m,
          ),
        );
        // Messages the session would not queue go first: the first as a turn, which takes
        // what the session holds before it, and the rest queued behind it. While a stop card
        // is open they wait for its answer, with the rest.
        if (!rt(id).stopCard && queuedRows(get, id).some((m) => m.queueState === 'held')) {
          void sendHeld(set, get, id);
        } else {
          void drain(set, get, id);
        }
      },

      async syncSession(id) {
        const conv = get().conversations[id];
        const service = getChatService();
        if (!conv?.sessionId || !service.readSession) return;
        const sessionId = conv.sessionId;
        // Once per page load, and never for a session this page bound: it reads its replies.
        if (syncedConversations.has(id) || boundSessions.has(sessionId)) return;
        syncedConversations.add(id);
        let live: LiveSession | null;
        try {
          live = await service.readSession(sessionId);
        } catch {
          return;
        }
        if (!live || get().conversations[id]?.sessionId !== sessionId) return;
        if (live.connected) markConnected(set, id);
        const r = rt(id);
        readView(r, live);
        // A send from this tab is being read meanwhile: its end says what is queued. This read may
        // predate its turn, so it holds nothing back: a compaction still running answers the send
        // 409, and the poll that follows reads it.
        if (r.streams > 0) {
          r.compactingSeen = false;
          publish(set, id);
          return;
        }
        publish(set, id);
        if (live.turnRunning || live.compacting === true) {
          reconcileQueue(set, id, live.queue, true);
          r.stopCard = live.stopCard?.questionId ?? null;
          placeStopCard(set, id, live.stopCard);
          // A compaction changes no turn record: following it reloads nothing, unless a turn
          // holds the session after it.
          follow(set, get, id, live.turnRunning || 'onTurn');
          return;
        }
        // A message the session held is gone from its queue: a turn no one read took it, so
        // the thread is shown from the live session, as the poll shows a followed turn.
        const listed = new Set(live.queue.map((e) => e.id));
        const behind = queuedRows(get, id).some(
          (m) => (m.queueState === undefined || m.queueState === 'stale') && !listed.has(m.id),
        );
        if (behind) {
          const restored = restoreTurns(sessionId, live.turns, conv.createdAt);
          patchMessages(set, id, (messages) => [
            ...withTakenFiles(withKeptFilesAndBlocks(restored, messages), messages, live.queue),
            ...messages.filter(isQueuedRow),
          ]);
        }
        turnEnded(set, get, id, live, true);
      },

      async compact(id) {
        const conv = get().conversations[id];
        const service = getChatService();
        if (!conv?.sessionId || !service.compactSession || isBusy(id)) return;
        const r = rt(id);
        if (r.compactOffFor === conv.modelId || r.compactHidden || r.sessionGone) return;
        const compactFn = service.compactSession.bind(service);
        const sessionId = conv.sessionId;
        r.pressing = true;
        r.outcome = null;
        publish(set, id);
        // The session is read meanwhile as for a running turn.
        ensurePoll(set, get, id);
        const controller = track(id);
        // One press, one outcome: the first call's tokens before, the last stored fold's after.
        let compacted: { before: number | null; after: number | null } | null = null;
        let outcome: CompactionOutcome | null = null;
        let waited = false;
        // The calls that stored a fold, and the newest cut they reached: a call that does not
        // move it forward ends the press.
        let rounds = 0;
        let lastCut: number | null = null;
        // The chat's model each call sends from: a model change between calls goes with the next.
        let modelId = conv.modelId;
        try {
          for (;;) {
            modelId = get().conversations[id]?.modelId ?? conv.modelId;
            const answer = await compactFn(sessionId, modelId, controller.signal);
            if (runtimes.get(id) !== r) return;
            if (answer.status === 'compacted') {
              const figures = answer.context;
              const after = figures?.afterTokens ?? null;
              compacted = {
                before: compacted ? compacted.before : (figures?.beforeTokens ?? null),
                after,
              };
              // The meter takes the fold's figure, an estimate, and the divider its cut. An
              // answer that names no window keeps the one the meter shows.
              if (after !== null) {
                r.context = {
                  usedTokens: after,
                  windowTokens: figures?.windowTokens ?? r.context?.windowTokens ?? null,
                  estimated: true,
                };
              }
              if (answer.throughTurn !== null) r.cut = answer.throughTurn;
              publish(set, id);
              // More waits to be folded: called again at once, with no new press, unless a call
              // left the cut where it was, and at most MAX_COMPACT_ROUNDS times. A fold that is
              // not a session turn's names no cut, and only the cap bounds it.
              rounds += 1;
              const advanced =
                answer.throughTurn === null || lastCut === null || answer.throughTurn > lastCut;
              if (answer.throughTurn !== null) lastCut = answer.throughTurn;
              if (answer.remainingTurns > 0 && advanced && rounds < MAX_COMPACT_ROUNDS) continue;
              break;
            }
            if (answer.status === 'busy' && !waited) {
              // A turn or an operation holds the session: once it is free, one more call. What
              // holds it is not this press, so a turn it was is shown, as for a send.
              waited = true;
              r.reloadOnEnd = true;
              const free = await waitUntilFree(set, get, id);
              if (runtimes.get(id) !== r) return;
              if (free) continue;
              r.sessionGone = true;
              break;
            }
            outcome = pressAnswer(r, answer, modelId);
            break;
          }
        } catch {
          // Aborted as the chat was deleted or cleared; anything else reads as no answer.
          if (runtimes.get(id) === r) outcome = pressAnswer(r, { status: 'unknown' }, modelId);
        } finally {
          untrack(id, controller);
        }
        if (runtimes.get(id) !== r) return;
        r.pressing = false;
        // This press's own compaction has ended, whatever the last read saw of it.
        r.compactingSeen = false;
        r.outcome = compacted ? { kind: 'compacted', ...compacted } : outcome;
        publish(set, id);
        // The session is read again, from now: a read sent while the press ran says nothing of
        // its end. While it shows a compaction (an answer that never came may follow one still
        // running), the chat waits; once it shows the session free, what waited for it goes.
        stopPoll(r);
        ensurePoll(set, get, id);
      },

      dismissCompactionNote(id) {
        const r = runtimes.get(id);
        if (!r?.outcome) return;
        r.outcome = null;
        publish(set, id);
      },
    }),
    {
      name: AI_CIO_STORAGE_KEY,
      storage: createJSONStorage(() => userScopedLocalStorage),
      // Only persist the conversation data, not the function members.
      partialize: (s) => ({
        conversations: s.conversations,
        order: s.order,
        activeId: s.activeId,
      }),
      // A reload mid-stream would otherwise rehydrate a message stuck in
      // 'streaming' (no AbortController, no code path to finish it). Demote any
      // such message to a terminal status on load.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.activeId !== null && !conversationById(state.conversations, state.activeId)) {
          state.activeId = null;
        }
        for (const conv of Object.values(state.conversations)) {
          conv.messages = conv.messages.map((stored): Message => {
            const m = withoutPreparing(stored);
            if (m.status === 'streaming') {
              return {
                ...m,
                status: m.content ? 'complete' : 'error',
                content: m.content || tr('message.interrupted', 'This response was interrupted.'),
                waiting: undefined,
                steps: m.steps?.map((step) =>
                  step.status === 'active' ? { ...step, status: 'stopped' } : step,
                ),
              };
            }
            // A queued message whose send the reload cut off: the session's queue says
            // where it is once the conversation is read (`syncSession`).
            if (m.queueState === 'sending') return { ...m, queueState: undefined };
            return m;
          });
        }
      },
    },
  ),
);

// ── streaming helper ─────────────────────────────────────────────────────────

type SetState = (
  partial:
    | Partial<ConversationsState>
    | ((state: ConversationsState) => Partial<ConversationsState>),
) => void;
type GetState = () => ConversationsState;

/** Produces the StreamChunk stream for a turn (a new message or an answer). */
type StreamFactory = (opts: SendOptions) => AsyncIterable<StreamChunk>;

interface OutputRef {
  outputId?: string;
  name: string;
}

type OutputVaultSave = Extract<VaultSave, { kind: 'output' }>;

function blockFileName(block: ContentBlock): string {
  const name = block.metadata?.filename;
  return typeof name === 'string' ? name : '';
}

function sameOutputName(a: string, b: string): boolean {
  return a.trim() !== '' && b.trim() !== '' && normalizeOutputName(a) === normalizeOutputName(b);
}

function isSameOutput(a: OutputRef, b: OutputRef): boolean {
  if (a.outputId && b.outputId) return a.outputId === b.outputId;
  return sameOutputName(a.name, b.name);
}

function fileBlockOutput(block: ContentBlock): OutputRef | undefined {
  return block.type === 'file'
    ? { outputId: block.outputId, name: blockFileName(block) }
    : undefined;
}

function hasFileBlockFor(blocks: readonly ContentBlock[], output: OutputRef): boolean {
  return blocks.some((block) => {
    const produced = fileBlockOutput(block);
    return produced !== undefined && isSameOutput(produced, output);
  });
}

function pendingKey(output: OutputRef): string {
  return output.outputId ?? `name:${normalizeOutputName(output.name)}`;
}

function withoutPreparing(message: Message): Message {
  if (!message.pendingFiles?.some((entry) => entry.state === 'preparing')) return message;
  const failed = message.pendingFiles.filter((entry) => entry.state === 'failed');
  return { ...message, pendingFiles: failed.length ? failed : undefined };
}

function outputBlockIndex(blocks: readonly ContentBlock[], save: OutputVaultSave): number {
  if (save.blockId) return blocks.findIndex((block) => block.id === save.blockId);
  const unclaimed = (block: ContentBlock) =>
    block.type === 'file' && (!block.vaultFileId || block.vaultFileId === save.vaultFileId);
  const byOutput = save.outputId
    ? blocks.findIndex((block) => unclaimed(block) && block.outputId === save.outputId)
    : -1;
  if (byOutput !== -1) return byOutput;
  const name = save.name ?? '';
  return blocks.findIndex(
    (block) =>
      unclaimed(block) &&
      !(block.outputId && save.outputId) &&
      sameOutputName(blockFileName(block), name),
  );
}

function heldSaveIndex(saves: readonly OutputVaultSave[], block: ContentBlock): number {
  const byId = saves.findIndex((save) => save.blockId === block.id);
  return byId !== -1 ? byId : saves.findIndex((save) => outputBlockIndex([block], save) === 0);
}

function hasEarlierBlock(messages: readonly Message[], exceptId: string, blockId: string): boolean {
  return messages.some(
    (message) => message.id !== exceptId && message.blocks?.some((block) => block.id === blockId),
  );
}

function withAttachedRefs(messages: Message[], refs: readonly AttachedRef[]): Message[] {
  const byId = new Map(refs.map((ref) => [ref.attachmentId, ref]));
  return messages.map((message) =>
    message.files?.some((file) => byId.has(file.id))
      ? {
          ...message,
          files: message.files.map((file) => {
            const ref = byId.get(file.id);
            if (!ref) return file;
            return {
              ...file,
              fileRef: ref.fileRef,
              ...(ref.vaultFileId ? { vaultFileId: ref.vaultFileId } : {}),
            };
          }),
        }
      : message,
  );
}

function withUploadVaultIds(messages: Message[], byRef: ReadonlyMap<string, string>): Message[] {
  const savedId = (file: AttachedFile) =>
    file.fileRef === undefined ? undefined : byRef.get(file.fileRef);
  return messages.map((message) =>
    message.files?.some((file) => savedId(file) !== undefined)
      ? {
          ...message,
          files: message.files.map((file) => {
            const vaultFileId = savedId(file);
            return vaultFileId ? { ...file, vaultFileId } : file;
          }),
        }
      : message,
  );
}

function withOutputVaultId(
  messages: Message[],
  exceptId: string,
  save: OutputVaultSave,
): Message[] {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (!message?.blocks || message.id === exceptId) continue;
    const at = message.blocks.findIndex((block) => block.id === save.blockId);
    if (at === -1) continue;
    const blocks = message.blocks.map((block, blockIndex) =>
      blockIndex === at ? { ...block, vaultFileId: save.vaultFileId } : block,
    );
    return messages.map((m, i) => (i === index ? { ...message, blocks } : m));
  }
  return messages;
}

/** How a stream that ran a turn ended: its SESSION_COMPLETE's `queued` and `queue_held`. */
interface TurnEnd {
  /** The terminal chunk failed the turn. */
  error: boolean;
  /** The session's queued messages as the turn ended; absent when the stream did not say. */
  queued?: number;
  queueHeld: boolean;
  /**
   * The stream raised a mode card (`turn_mode_v1`): its `queue_held` is the orchestrator holding
   * the queue for the card's answer, not a stop.
   */
  modeCard: boolean;
}

/** Where one request's reply goes, and what happens when the server answers otherwise. */
interface StreamPlan {
  /** The reply the turn streams into. A MESSAGE_TAKEN mid-turn moves it to a new reply. */
  target: string;
  /** The request's own message: messages a turn takes at its opening show before it. */
  anchor?: string;
  /**
   * A message the request sends again, which keeps its place in the thread (a regenerate's
   * prompt): the turn's id goes on it, as on the request's own message.
   */
  resent?: string;
  /** Context for the request (a regenerate passes the history as of its turn). */
  history?: Message[];
  /** The id the message is queued under while a turn runs (see SendOptions). */
  clientMessageId?: string;
  /** The model the request sends, when not the conversation's (a Connect card's answer). */
  modelId?: string;
  /** Drop the request's own messages when its stream ran no turn (a drain, a card answer). */
  dropIfIdle?: boolean;
  /**
   * The request is read as a stream of the session only once the server gives it a turn: a
   * queued send that is answered in JSON never holds back the end of the turn it waits behind.
   */
  countsWhenStarted?: boolean;
  /** The server took the request: its turn streams. */
  onAccepted?: () => void;
  /** The request ran no turn. Without this, the reply shows it as a failure. */
  onAnswer?: (answer: TurnAnswer) => void;
  /** The request failed before any stream. Without this, the reply shows the failure. */
  onFailed?: (message: string) => void;
  /** Called once, as soon as the server has answered the request (or it failed). */
  onSettled?: () => void;
}

/** The reply being streamed, as the chunks so far build it. */
interface ReplyDraft {
  text: string;
  blocks: NonNullable<Message['blocks']>;
  blockOrder?: string[];
  /** What the reply's steps brought back, from the TASK_COMPLETEDs that said. */
  health?: Message['health'];
  sources?: Message['sources'];
  interaction?: Message['pendingInteraction'];
  proposal?: Message['pendingProposal'];
  /** A Connect card arrived (it goes on the reply's `connectCards` at once). */
  connect?: boolean;
  /**
   * The status line: the latest one, until text arrives after it (the next one
   * brings it back) or the turn ends.
   */
  statusText?: string;
}

async function streamInto(
  set: SetState,
  get: GetState,
  conversationId: string,
  plan: StreamPlan,
  makeStream: StreamFactory,
): Promise<void> {
  const conv = get().conversations[conversationId];
  if (!conv) {
    plan.onSettled?.();
    return;
  }

  const r = rt(conversationId);
  const controller = track(conversationId);
  endHold(set, get, conversationId);
  let counted = !plan.countsWhenStarted;
  if (counted) r.streams += 1;
  publish(set, conversationId);

  // Regenerating an older message passes the context as of that turn — later
  // messages exist in the UI but must not leak into the context (they hadn't
  // happened yet at this point in the conversation).
  const history = plan.history ?? conv.messages.filter((m) => m.id !== plan.target);

  // Read `plan.target` on every patch: an intake moves the reply to a new message.
  const patchReply = (patch: (m: Message) => Message) => {
    const target = plan.target;
    set((s) => {
      const c = s.conversations[conversationId];
      if (!c) return s;
      return {
        conversations: {
          ...s.conversations,
          [conversationId]: {
            ...c,
            messages: c.messages.map((m) => (m.id === target ? patch(m) : m)),
            updatedAt: Date.now(),
          },
        },
      };
    });
  };

  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    plan.onSettled?.();
  };

  let reply: ReplyDraft = { text: '', blocks: [] };
  // The server streams this request a turn (it was not refused or answered in JSON).
  let started = false;
  let progressed = false;
  // The turn did something; a request whose stream did nothing ran no turn.
  let opened = false;
  let stopped = false;
  // The turn raised a mode card: a held queue waits for its answer.
  let modeCard = false;
  // True once a terminal chunk has been processed — the turn genuinely ended
  // (complete or failed) before any abort, so it must not read as cancelled.
  let finished = false;
  let aborted = false;
  let end: TurnEnd | undefined;
  let answer: TurnAnswer | undefined;
  let failure: string | undefined;
  // What the turn runs with (its TURN_MODE): every answer bubble it writes carries it.
  let ran: RanWith | undefined;
  // The rows the request's turn wrote besides its current reply and its own message: replies an
  // intake closed and the queued messages it took. Its turn id goes on all of them.
  const written = new Set<string>();
  try {
    let steps: ProcessingStep[] | undefined;
    let waiting: Message['waiting'];
    let route: Message['route'];
    let workedForMs: number | undefined;
    let processingFailure: Message['failure'];
    let pending: PendingResultFile[] = [];
    let heldSaves: OutputVaultSave[] = [];
    for await (const chunk of makeStream({
      history,
      modelId: plan.modelId ?? conv.modelId,
      sessionModelUnknown: isSessionModelUnknown(conv),
      connected: conv.connected,
      thinking: chatChoice(conv),
      agentic: conv.agentic === true,
      sessionId: conv.sessionId,
      signal: controller.signal,
      clientMessageId: plan.clientMessageId,
      investorData: conv.investorData,
    })) {
      // Aborted mid-flight (the chat is being deleted or cleared): stop applying
      // chunks. A late chunk — or the synthetic done the SSE reader emits when it
      // notices the abort — must not overwrite the 'cancelled' state.
      if (controller.signal.aborted) break;

      if (chunk.investorData !== undefined) {
        markInvestorData(set, conversationId, chunk.investorData);
        continue;
      }

      // The request ran no turn: the plan says what becomes of it.
      if (chunk.answer) {
        answer = chunk.answer;
        break;
      }

      // Bind the backend's session id to the conversation (and the CRM history
      // index). Also rebinds when the service reports a DIFFERENT id — the
      // recovery path that replaces a stale/rejected session with a fresh one;
      // the replaced id is kept so a delete still erases it. Either way this send
      // created the session, so any model it holds was sent from this chat (see
      // Conversation.sessionStartedHere). The new session is synced once its index
      // row is accepted.
      if (chunk.sessionId && get().conversations[conversationId]?.sessionId !== chunk.sessionId) {
        const boundId = chunk.sessionId;
        const title = get().conversations[conversationId]?.title ?? tr('history.new', 'New chat');
        // Turn ids start again in the fresh session: the rows' ids, the cut and the context of
        // the one it replaces say nothing of it.
        if (get().conversations[conversationId]?.sessionId) {
          r.context = null;
          r.cut = null;
          r.compactOffFor = null;
          r.compactHidden = false;
          r.sessionGone = false;
          publish(set, conversationId);
        }
        set((s) => {
          const c = s.conversations[conversationId];
          if (!c) return s;
          return {
            conversations: {
              ...s.conversations,
              [conversationId]: {
                ...c,
                sessionId: boundId,
                sessionStartedHere: true,
                synced: false,
                ...(c.sessionId
                  ? {
                      priorSessionIds: [...(c.priorSessionIds ?? []), c.sessionId],
                      messages: c.messages.map(({ turnId: _turnId, ...m }): Message => m),
                    }
                  : {}),
              },
            },
          };
        });
        boundSessions.add(boundId);
        void onSessionBound?.(boundId, title).then((accepted) => {
          if (accepted) markSynced(set, conversationId, boundId);
        });
      }

      if (chunk.attached?.length) {
        const refs = chunk.attached;
        patchMessages(set, conversationId, (messages) => withAttachedRefs(messages, refs));
        if (!chunk.done) continue;
      }

      if (!started) {
        // Failed before any stream: a queued message says so on its row.
        if (chunk.done && chunk.error && plan.onFailed) {
          failure = chunk.delta;
          break;
        }
        const layerProgress =
          !chunk.done && !chunk.delta && (chunk.step !== undefined || chunk.waiting !== undefined);
        if (layerProgress) {
          progressed = true;
          r.preflight = true;
        }
        if (!(chunk.done && chunk.error) && !layerProgress) {
          started = true;
          r.preflight = false;
          if (!counted) {
            r.streams += 1;
            counted = true;
            publish(set, conversationId);
          }
          settle();
          // A turn this request opens closes an open mode card, or answers it.
          r.modeHold = false;
          // The server gave this request a turn, so a turn this tab followed has ended.
          endFollow(set, get, conversationId, plan);
          plan.onAccepted?.();
          if (r.stopWhenBound) {
            // Stop was pressed before the session was bound: this turn holds it now.
            r.stopWhenBound = false;
            r.stopping = false;
            void get().stopTurn(conversationId);
          }
        }
      }

      if (chunk.taken) {
        opened = true;
        written.add(plan.target);
        for (const m of chunk.taken) written.add(m.id);
        const closingTarget = plan.target;
        takeMessages(set, conversationId, plan, chunk.taken, chunk.intake === true);
        // Mid-turn the reply so far closed, and the turn goes on in a new one, which ran as
        // the turn did.
        if (chunk.intake) {
          reply = { text: '', blocks: [] };
          pending = pending.filter((entry) => entry.state === 'preparing');
          if (ran) {
            const turnRan = ran;
            patchReply((m) => ({ ...m, ran: turnRan }));
          }
          if (steps && closingTarget !== plan.target) {
            const moved = steps.map((step) => ({ ...step }));
            patchMessages(set, conversationId, (messages) =>
              messages.map(
                (m): Message =>
                  m.id === closingTarget ? { ...m, steps: undefined, waiting: undefined } : m,
              ),
            );
            patchReply((m) => ({ ...m, steps: moved }));
          }
        }
        continue;
      }
      if (chunk.resolved) {
        retireCard(set, conversationId, chunk.resolved);
        continue;
      }
      if (chunk.modeNote) {
        // The reply says the turn ran at another level than asked; later patches keep it.
        const modeNote = chunk.modeNote;
        patchReply((m) => ({ ...m, modeNote }));
        continue;
      }
      if (chunk.ran) {
        const turnRan = chunk.ran;
        ran = turnRan;
        patchReply((m) => ({ ...m, ran: turnRan }));
        continue;
      }
      if (chunk.connected) {
        markConnected(set, conversationId);
        continue;
      }
      if (chunk.stopped) {
        opened = true;
        stopped = true;
        r.stopping = false;
        publish(set, conversationId);
        continue;
      }

      if (chunk.block) {
        const block = chunk.block;
        const at = reply.blocks.findIndex((b) => b.id === block.id);
        const previous = at === -1 ? undefined : reply.blocks[at];
        let next =
          previous?.vaultFileId && !block.vaultFileId
            ? { ...block, vaultFileId: previous.vaultFileId }
            : block;
        const held = heldSaveIndex(heldSaves, next);
        const heldSave = held === -1 ? undefined : heldSaves[held];
        if (heldSave) {
          next = { ...next, vaultFileId: heldSave.vaultFileId };
          heldSaves = heldSaves.filter((_, index) => index !== held);
        }
        if (at === -1) reply.blocks.push(next);
        else reply.blocks[at] = next;
        const produced = fileBlockOutput(next);
        if (produced) pending = pending.filter((entry) => !isSameOutput(entry, produced));
      }

      if (chunk.vaultSaves?.length) {
        const uploads = new Map<string, string>();
        for (const save of chunk.vaultSaves) {
          if (save.kind === 'upload') {
            uploads.set(save.fileRef, save.vaultFileId);
            continue;
          }
          const at = outputBlockIndex(reply.blocks, save);
          const found = at === -1 ? undefined : reply.blocks[at];
          if (found) reply.blocks[at] = { ...found, vaultFileId: save.vaultFileId };
          else if (
            save.blockId &&
            hasEarlierBlock(
              get().conversations[conversationId]?.messages ?? [],
              plan.target,
              save.blockId,
            )
          ) {
            const current = plan.target;
            patchMessages(set, conversationId, (messages) =>
              withOutputVaultId(messages, current, save),
            );
          } else heldSaves.push(save);
        }
        if (uploads.size) {
          patchMessages(set, conversationId, (messages) => withUploadVaultIds(messages, uploads));
        }
      }

      for (const output of chunk.preparing ?? []) {
        if (hasFileBlockFor(reply.blocks, output)) continue;
        const at = pending.findIndex((entry) => isSameOutput(entry, output));
        const outputId = output.outputId ?? pending[at]?.outputId;
        const entry: PendingResultFile = {
          key: pendingKey({ outputId, name: output.name }),
          name: output.name,
          state: 'preparing',
          taskId: output.taskId,
          ...(outputId ? { outputId } : {}),
        };
        if (at === -1) pending.push(entry);
        else pending[at] = entry;
      }

      for (const outputFailure of chunk.outputFailures ?? []) {
        const at = pending.findIndex((entry) => isSameOutput(entry, outputFailure));
        const existing = at === -1 ? undefined : pending[at];
        if (existing) {
          pending[at] = { ...existing, state: 'failed' };
          continue;
        }
        if (!outputFailure.name.trim() || hasFileBlockFor(reply.blocks, outputFailure)) continue;
        pending.push({
          key: pendingKey(outputFailure),
          name: outputFailure.name,
          state: 'failed',
          ...(outputFailure.outputId ? { outputId: outputFailure.outputId } : {}),
          ...(outputFailure.taskId ? { taskId: outputFailure.taskId } : {}),
        });
      }

      if (chunk.done) pending = pending.filter((entry) => entry.state === 'failed');

      if (chunk.blockOrder) reply.blockOrder = chunk.blockOrder;
      if (chunk.health) reply.health = [...(reply.health ?? []), chunk.health];
      if (chunk.sources) reply.sources = withSources(reply.sources, chunk.sources);
      if (chunk.interaction) {
        reply.interaction = chunk.interaction;
        // The stop card holds the queue until it is answered or a message moves on.
        if (chunk.interaction.flow === STOP_CARD_FLOW) r.stopCard = chunk.interaction.questionId;
        if (chunk.interaction.flow === MODE_CARD_FLOW) modeCard = true;
      }
      if (chunk.proposal?.schema === CONNECT_CARD_SCHEMA) {
        // A Connect card or notice joins the reply's cards, never `pendingProposal`, which a
        // later card would overwrite; it answers with the model the conversation has now.
        reply.connect = true;
        const card: ConnectCardRecord = {
          proposal: chunk.proposal,
          modelId: get().conversations[conversationId]?.modelId ?? conv.modelId,
          arrivedAt: Date.now(),
          // A notice opens no interaction and nothing answers it (CC D7), and a card resolved
          // before it showed is done: each arrives as a record, so its reply keeps its actions.
          ...(chunk.proposal.content.state === 'unavailable'
            ? { record: true, badge: 'unavailable' as const }
            : resolvedCards.get(conversationId)?.has(chunk.proposal.proposalId)
              ? { record: true }
              : {}),
        };
        patchReply((m) => ({
          ...m,
          connectCards: [
            ...(m.connectCards ?? []).filter(
              (c) => c.proposal.proposalId !== card.proposal.proposalId,
            ),
            card,
          ],
        }));
      } else if (chunk.proposal) {
        reply.proposal = chunk.proposal;
      }
      if (chunk.status) reply.statusText = chunk.status;
      if (chunk.delta || chunk.done) reply.statusText = undefined;
      if (
        chunk.delta ||
        chunk.block ||
        chunk.status ||
        chunk.interaction ||
        chunk.proposal ||
        chunk.health ||
        chunk.sources ||
        chunk.vaultSaves?.length ||
        chunk.preparing?.length ||
        chunk.outputFailures?.length
      ) {
        opened = true;
      }
      reply.text += chunk.delta;
      const snapshot = reply.text;
      const snapshotBlocks = [...reply.blocks];
      const snapshotOrder = reply.blockOrder ? [...reply.blockOrder] : undefined;
      // A reply no frame said anything about keeps neither field, as before.
      const snapshotHealth = reply.health ? { health: [...reply.health] } : {};
      const snapshotSources = reply.sources ? { sources: [...reply.sources] } : {};
      const snapshotInteraction = reply.interaction;
      const snapshotProposal = reply.proposal;
      const snapshotPending = pending.length ? pending.map((entry) => ({ ...entry })) : undefined;
      const statusText = reply.statusText;
      const hasContent = Boolean(
        snapshot ||
        snapshotBlocks.length ||
        snapshotInteraction ||
        snapshotProposal ||
        reply.connect ||
        pending.some((entry) => entry.state === 'failed'),
      );
      if (chunk.done) {
        finished = true;
        end = {
          error: chunk.error === true,
          queued: chunk.queued,
          queueHeld: chunk.queueHeld === true,
          modeCard,
        };
      }
      if (chunk.step) steps = upsertStep(steps ?? [], chunk.step);
      if (chunk.waiting !== undefined) waiting = chunk.waiting ?? undefined;
      if (chunk.route) route = chunk.route;
      if (chunk.workedForMs !== undefined) workedForMs = chunk.workedForMs;
      if (chunk.failure) processingFailure = chunk.failure;
      if (chunk.done) waiting = undefined;
      const snapshotSteps = steps ? steps.map((step) => ({ ...step })) : undefined;
      patchReply((m) => ({
        ...m,
        content: snapshot,
        blocks: snapshotBlocks,
        blockOrder: snapshotOrder,
        ...snapshotHealth,
        ...snapshotSources,
        pendingInteraction: snapshotInteraction,
        pendingProposal: snapshotProposal,
        pendingFiles: snapshotPending,
        statusText,
        steps: snapshotSteps,
        waiting,
        route,
        workedForMs,
        failure: processingFailure,
        status: chunk.done
          ? chunk.error
            ? 'error'
            : stopped
              ? 'stopped'
              : 'complete'
          : 'streaming',
        ...(chunk.done && chunk.error && !hasContent && !processingFailure
          ? { content: tr('message.error', 'Something went wrong generating a response.') }
          : {}),
      }));
      if (chunk.done) {
        if (chunk.turnId !== undefined) {
          stampTurn(
            set,
            conversationId,
            [...written, plan.target, plan.anchor, plan.resent],
            chunk.turnId,
          );
        }
        if (chunk.context !== undefined) r.context = chunk.context;
        if (chunk.compactedThroughTurn !== undefined) r.cut = chunk.compactedThroughTurn;
        publish(set, conversationId);
      }
    }
    if (controller.signal.aborted && !finished) aborted = true;
  } catch (err) {
    if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
      aborted = true;
    } else if (!started && plan.onFailed) {
      failure = '';
    } else {
      patchReply((m) => ({
        ...withoutPreparing(m),
        status: 'error',
        statusText: undefined,
        waiting: undefined,
        steps: m.steps?.map((step) =>
          step.status === 'active' ? { ...step, status: 'failed' } : step,
        ),
        content: m.content || tr('message.error', 'Something went wrong generating a response.'),
      }));
    }
  } finally {
    untrack(conversationId, controller);
    if (counted) r.streams -= 1;
    r.preflight = false;
  }
  settle();
  if (!started && r.stopWhenBound) {
    // No turn started for the stop to land on.
    r.stopWhenBound = false;
    r.stopping = false;
  }

  // A chat deleted or cleared mid-stream aborts it — a neutral 'cancelled' state
  // (partial content kept), never an error or an empty-reply fallback. A turn
  // that already ended is left as it ended.
  if (aborted) {
    markCancelled(patchReply);
    publish(set, conversationId);
    return;
  }
  // Its turns were forgotten meanwhile (the chat was deleted or dropped).
  if (runtimes.get(conversationId) !== r) return;

  if (answer) {
    publish(set, conversationId);
    if (plan.onAnswer) {
      plan.onAnswer(answer);
    } else {
      // A request that does not queue was refused: that is a failed reply.
      patchReply((m) => ({
        ...m,
        status: 'error',
        statusText: undefined,
        content: m.content || tr('message.error', 'Something went wrong generating a response.'),
      }));
    }
    return;
  }
  if (failure !== undefined) {
    publish(set, conversationId);
    plan.onFailed?.(failure);
    return;
  }

  if (plan.dropIfIdle && started && !opened && end && !end.error) {
    // It ran no turn: a drain whose queue had emptied, a `keep` on an offer card, or
    // an answer to a card another turn had closed. Its own messages go.
    removeMessages(set, conversationId, [plan.target, ...(plan.anchor ? [plan.anchor] : [])]);
  } else {
    // Finalize, and never leave a blank bubble: a completed turn with no text,
    // no blocks, and no open question gets a clear fallback message.
    patchReply((current) => {
      const m = withoutPreparing(current);
      const empty =
        !m.content &&
        !(m.blocks && m.blocks.length) &&
        !m.pendingInteraction &&
        !m.pendingProposal &&
        !m.pendingFiles?.length &&
        !m.connectCards?.length;
      return {
        ...m,
        status: m.status === 'streaming' ? 'complete' : m.status,
        content:
          empty && m.status !== 'stopped' && !m.failure
            ? tr('message.empty', 'No response was returned. Please try again.')
            : m.content,
      };
    });
  }
  onTurnFinished?.(conversationId);
  if (started || progressed) afterTurn(set, get, conversationId, end);
  else publish(set, conversationId);
}

/** Puts the record a request's turn ended with on the rows it wrote: its divider's anchor. */
function stampTurn(
  set: SetState,
  conversationId: string,
  ids: (string | undefined)[],
  turnId: number,
): void {
  const rows = new Set(ids);
  patchMessages(set, conversationId, (messages) =>
    messages.map((m): Message => (rows.has(m.id) ? { ...m, turnId } : m)),
  );
}

function upsertStep(steps: ProcessingStep[], step: ProcessingStep): ProcessingStep[] {
  const index = steps.findIndex((s) => s.id === step.id);
  if (index === -1) return [...steps, step];
  return steps.map((s, i) => (i === index ? { ...s, ...step } : s));
}

/** Marks a conversation synced once the history index accepted its session's row. */
function markSynced(set: SetState, conversationId: string, sessionId: string): void {
  set((s) => {
    const c = s.conversations[conversationId];
    if (c?.sessionId !== sessionId) return s;
    return { conversations: { ...s.conversations, [conversationId]: { ...c, synced: true } } };
  });
}

/**
 * Marks the in-flight assistant message as 'cancelled' — a neutral, expected
 * terminal state (its chat was deleted or cleared while it streamed), not a red
 * error. Any partial content already streamed is kept; the row renders a muted
 * "Cancelled" note. Only a message still mid-stream is demoted — one that
 * already reached a terminal state is left.
 */
function markCancelled(patch: (fn: (m: Message) => Message) => void): void {
  patch((m) =>
    withoutPreparing(
      m.status === 'streaming'
        ? {
            ...m,
            status: 'cancelled',
            waiting: undefined,
            steps: m.steps?.map((step) =>
              step.status === 'active' ? { ...step, status: 'stopped' } : step,
            ),
          }
        : m,
    ),
  );
}

// ── turns: the queue, the stop and the poll ─────────────────────────────────
//
// A session runs one turn at a time. A message sent while a turn runs is queued
// on the orchestrator (`queue: true`) and shows as a queued row, last in the
// thread, until a turn takes it: the running turn at its next step, or the next
// turn, which this store starts (a drain) once the running one ends. Stop asks
// the orchestrator to stop the turn, and its stream goes on to the stop card, or,
// when nothing had finished, to one line that says so; either way the stop holds
// the queue (`queue_held`), for the card's answer or, with no card, for the user's
// next message or "Send now". A typed phrase's mode card holds it too, on the
// orchestrator, until the card's answer or a later message: the rows stay queued,
// with no "Send now", and nothing drains. A turn this tab does not read (another
// tab's, or one a reload found running) is followed by polling the session until
// it ends. iq-ai-cio: docs/features/chat-api.md#stop-and-the-queue.

/** How often a session is read while a turn this tab does not read runs in it. */
const POLL_MS = 3000;

/** On load, a message queued longer ago than this waits for "Send now" instead of running. */
const QUEUE_AUTO_DRAIN_MAX_AGE_MS = 600_000;

/** The stop card's flow: it holds the queue until it is answered. */
const STOP_CARD_FLOW = 'turn_stop_v1';

/**
 * The flow of a typed phrase's mode card, which holds the queue on the orchestrator until it is
 * answered, and of the offer of a deeper pass, which holds nothing.
 */
const MODE_CARD_FLOW = 'turn_mode_v1';

/**
 * The most calls one compaction press makes while the server says more waits to be folded: a
 * server that keeps saying so stops here, and the user can press again.
 */
const MAX_COMPACT_ROUNDS = 10;

/** What this tab knows of one conversation's turns (not persisted). */
interface TurnRuntime {
  /** Requests whose replies this tab is reading. */
  streams: number;
  /** A turn this tab does not read holds the session; the poll follows it. */
  following: boolean;
  /** Show the conversation from the live session once the followed turn ends. */
  reloadOnEnd: boolean;
  /**
   * The wait began on a compaction, which changes no turn record: once a read shows a turn
   * holding the session, it shows the conversation from the live session when that turn ends.
   */
  reloadOnTurn: boolean;
  /** Stop was pressed and the turn has not ended. */
  stopping: boolean;
  /** The stop request has not answered: no drain starts, or the stop could land on it. */
  stopInFlight: boolean;
  /** A drain waits for the stop request to answer. */
  drainAfterStop: boolean;
  /** Stop was pressed before the session was bound: it is asked for once the turn starts. */
  stopWhenBound: boolean;
  preflight: boolean;
  /** The open stop card's id: the queue waits for its answer. */
  stopCard: string | null;
  /**
   * A typed phrase's mode card is open: the orchestrator holds the queue for its answer, so
   * nothing drains and no row offers "Send now". The next turn this tab opens ends it.
   */
  modeHold: boolean;
  /** The card whose answer is being sent. */
  answering: string | null;
  /** A card answer that met a running turn, sent once that turn ends. */
  pendingAnswer: { cardId: string; send: () => void } | null;
  /** A poll is scheduled or reading. */
  polling: boolean;
  pollTimer: ReturnType<typeof setTimeout> | null;
  /** Bumped to discard a poll whose read is in flight. */
  pollGen: number;
  /**
   * The newest context the conversation holds (the meter's figure): SESSION_COMPLETE's, a
   * session read's, or a compaction's answer, whichever came last. Never persisted.
   */
  context: ContextFigure | null;
  /** The newest cut, from the same sources: the last turn the stored compaction folded. */
  cut: number | null;
  /** The last session read showed a compaction holding the session, and no turn. */
  compactingSeen: boolean;
  /** This tab's compaction press runs: its calls, its wait and its follow-up calls. */
  pressing: boolean;
  /** What this tab's last press came to, until Dismiss, the chat's next Send or a lowering pick. */
  outcome: CompactionOutcome | null;
  /**
   * The chat's model a press answered `internal_model_off` for: the control is held while the chat
   * keeps that model, until the page loads again. Only a press on a model the router does not run
   * meets that answer, so another model may compact.
   */
  compactOffFor: string | null;
  /** A press answered that compaction is not offered on the session: the control is left out. */
  compactHidden: boolean;
  /** A press answered 404: the meter, its control and the divider hide. */
  sessionGone: boolean;
  /** A press waiting for the session to be free: true once a read shows it, false once it is gone. */
  freeWaiters: ((free: boolean) => void)[];
}

const runtimes = new Map<string, TurnRuntime>();

// The files of held messages, by message id: of one held before its conversation had a session,
// which no request has sent yet, and of one the session was too busy to take, which may not all
// have attached.
const heldFiles = new Map<string, AttachedFile[]>();

function rt(conversationId: string): TurnRuntime {
  let r = runtimes.get(conversationId);
  if (!r) {
    r = {
      streams: 0,
      following: false,
      reloadOnEnd: false,
      reloadOnTurn: false,
      stopping: false,
      stopInFlight: false,
      drainAfterStop: false,
      stopWhenBound: false,
      preflight: false,
      stopCard: null,
      modeHold: false,
      answering: null,
      pendingAnswer: null,
      polling: false,
      pollTimer: null,
      pollGen: 0,
      context: null,
      cut: null,
      compactingSeen: false,
      pressing: false,
      outcome: null,
      compactOffFor: null,
      compactHidden: false,
      sessionGone: false,
      freeWaiters: [],
    };
    runtimes.set(conversationId, r);
  }
  return r;
}

/**
 * A compaction holds the session, as this tab knows it: its own press runs, or the last session
 * read showed one. It counts as busy (iq-ai-cio `docs/features/chat-api.md#stop-and-the-queue`):
 * the poll goes on, and nothing held, queued or drained is sent, until a read shows it ended.
 */
function isCompacting(r: TurnRuntime): boolean {
  return r.pressing || r.compactingSeen;
}

/**
 * The session is busy: a reply is being written (one this tab reads, or one it follows), or a
 * compaction holds it.
 */
function isBusy(conversationId: string): boolean {
  const r = runtimes.get(conversationId);
  return r !== undefined && (r.streams > 0 || r.following || isCompacting(r));
}

/**
 * Shows what the conversation's reply is doing: its Stop, "Stopping…" and "Still answering", a
 * compaction holding it (no Stop, no "Still answering"), and the meter's figure and the cut.
 */
function publish(set: SetState, conversationId: string): void {
  const r = runtimes.get(conversationId);
  const compacting = r !== undefined && isCompacting(r);
  const next: TurnActivity = {
    busy: r !== undefined && (r.streams > 0 || r.following || compacting),
    stopping: r?.stopping ?? false,
    following: r !== undefined && r.following && !compacting,
    compacting,
    context: r?.context ?? null,
    compactedThroughTurn: r?.cut ?? null,
    compactionNote: r?.outcome ?? null,
    compactOffFor: r?.compactOffFor ?? null,
    compactHidden: r?.compactHidden ?? false,
    sessionGone: r?.sessionGone ?? false,
  };
  set((s) => {
    const prev = s.activity[conversationId];
    if (
      prev &&
      prev.busy === next.busy &&
      prev.stopping === next.stopping &&
      prev.following === next.following &&
      prev.compacting === next.compacting &&
      prev.context === next.context &&
      prev.compactedThroughTurn === next.compactedThroughTurn &&
      prev.compactionNote === next.compactionNote &&
      prev.compactOffFor === next.compactOffFor &&
      prev.compactHidden === next.compactHidden &&
      prev.sessionGone === next.sessionGone
    ) {
      return s;
    }
    return { activity: { ...s.activity, [conversationId]: next } };
  });
}

/**
 * What a session read says of the meter's figure, the cut and a compaction holding the session:
 * each only when the view sends it (an older orchestrator sends none).
 */
function readView(r: TurnRuntime, live: LiveSession): void {
  if (live.context !== undefined) r.context = live.context;
  if (live.compactedThroughTurn !== undefined) r.cut = live.compactedThroughTurn;
  r.compactingSeen = live.compacting === true && !live.turnRunning;
}

/** Waits until a session read shows no turn and no compaction: false once the session is gone. */
function waitUntilFree(set: SetState, get: GetState, conversationId: string): Promise<boolean> {
  const r = rt(conversationId);
  return new Promise((resolve) => {
    r.freeWaiters.push(resolve);
    ensurePoll(set, get, conversationId);
  });
}

/** Answers the presses waiting for the session to be free. */
function settleWaiters(r: TurnRuntime, free: boolean): void {
  for (const resolve of r.freeWaiters.splice(0)) resolve(free);
}

/**
 * What a compaction's answer does besides a stored fold (iq-ai-cio `compaction-route` §10), and
 * the outcome its note says, if any. `internal_model_off` holds the control while the chat keeps
 * `modelId`, the model the refused call was sent from. A 409 that compaction is not offered on the
 * session leaves the control out, and a 404 hides the meter: neither says anything. Any answer it
 * does not know (Cloud Run's 504, a request with no answer) says to try again shortly.
 */
function pressAnswer(
  r: TurnRuntime,
  answer: CompactAnswer,
  modelId: string,
): CompactionOutcome | null {
  switch (answer.status) {
    case 'nothing_to_compact':
      return { kind: 'nothing' };
    case 'not_offered':
      if (answer.reason === 'internal_model_off') {
        r.compactOffFor = modelId;
        return { kind: 'internalOff' };
      }
      if (answer.reason === 'platform' || answer.reason === 'connected') {
        r.compactHidden = true;
        return null;
      }
      break;
    case 'declined':
      return { kind: 'declined' };
    case 'unavailable':
      return { kind: 'unavailable' };
    case 'failed':
      return { kind: 'failed' };
    case 'gone':
      r.sessionGone = true;
      return null;
    default:
      break;
  }
  return { kind: 'unavailable' };
}

/** The newest turn id the rows hold, or null when none holds one. */
function newestTurnId(rows: readonly { turnId?: number }[]): number | null {
  let newest: number | null = null;
  for (const row of rows) {
    if (row.turnId !== undefined && (newest === null || row.turnId > newest)) newest = row.turnId;
  }
  return newest;
}

/**
 * A wait's end shows the thread from the session view only when the view holds a turn the thread
 * does not (a thread that holds no turn id, as before): a compaction changes no turn record, and
 * the rebuild keeps role, content and status, and of files, blocks and pending files only what
 * the view carries or the replaced row held (of the same turn and role, or a queued message a
 * newer turn took), so it would drop every reply's data health, sources, feedback, cards and turn
 * ids.
 */
function viewIsAhead(messages: Message[], live: LiveSession): boolean {
  const held = newestTurnId(messages);
  if (held === null) return true;
  const shown = newestTurnId(live.turns);
  return shown !== null && shown > held;
}

function track(conversationId: string): AbortController {
  const controller = new AbortController();
  const requests = abortControllers.get(conversationId) ?? new Set<AbortController>();
  requests.add(controller);
  abortControllers.set(conversationId, requests);
  return controller;
}

function untrack(conversationId: string, controller: AbortController): void {
  const requests = abortControllers.get(conversationId);
  if (!requests) return;
  requests.delete(controller);
  if (requests.size === 0) abortControllers.delete(conversationId);
}

/**
 * Aborts a conversation's requests and forgets its turns, as it is deleted or
 * dropped. An abort stops nothing on the server: a delete refused while a turn
 * runs asks it to stop (`ChatService.deleteSession`).
 */
function abortConversation(set: SetState, conversationId: string): void {
  for (const controller of abortControllers.get(conversationId) ?? []) controller.abort();
  abortControllers.delete(conversationId);
  const r = runtimes.get(conversationId);
  if (r) {
    stopPoll(r);
    settleWaiters(r, false);
  }
  runtimes.delete(conversationId);
  publish(set, conversationId);
}

function isQueuedRow(m: Message): boolean {
  return m.status === 'queued';
}

/** Adds messages to the thread before its queued rows, which stay last. */
function beforeQueued(messages: Message[], added: Message[]): Message[] {
  const at = messages.findIndex(isQueuedRow);
  return at === -1
    ? [...messages, ...added]
    : [...messages.slice(0, at), ...added, ...messages.slice(at)];
}

function newReply(id: string): Message {
  return { id, role: 'assistant', content: '', createdAt: Date.now(), status: 'streaming' };
}

/** Rewrites a conversation's messages; a no-op once it is gone. */
function patchMessages(
  set: SetState,
  conversationId: string,
  patch: (messages: Message[]) => Message[],
): void {
  set((s) => {
    const c = s.conversations[conversationId];
    if (!c) return s;
    return {
      conversations: {
        ...s.conversations,
        [conversationId]: { ...c, messages: patch(c.messages) },
      },
    };
  });
}

function removeMessages(set: SetState, conversationId: string, ids: string[]): void {
  patchMessages(set, conversationId, (messages) => messages.filter((m) => !ids.includes(m.id)));
}

/** Removes queued rows; one a turn has taken meanwhile is a sent message and stays. */
function removeQueuedRows(set: SetState, conversationId: string, ids: string[]): void {
  for (const id of ids) heldFiles.delete(id);
  patchMessages(set, conversationId, (messages) =>
    messages.filter((m) => !(isQueuedRow(m) && ids.includes(m.id))),
  );
}

/** Updates a queued row while it is one (a turn may have taken it meanwhile). */
function setRow(
  set: SetState,
  conversationId: string,
  rowId: string,
  patch: Pick<Message, 'queueState' | 'queueNote' | 'waitsForSendNow'>,
): void {
  patchMessages(set, conversationId, (messages) =>
    messages.map((m) => (m.id === rowId && isQueuedRow(m) ? { ...m, ...patch } : m)),
  );
}

function queuedRows(get: GetState, conversationId: string): Message[] {
  return get().conversations[conversationId]?.messages.filter(isQueuedRow) ?? [];
}

/** Drops the queued rows the session held: a turn took them, or they were removed. */
function dropServerRows(set: SetState, conversationId: string): void {
  patchMessages(set, conversationId, (messages) =>
    messages.filter(
      (m) => !(isQueuedRow(m) && (m.queueState === undefined || m.queueState === 'stale')),
    ),
  );
}

/**
 * The session's queue as the thread shows it. A row the session lists keeps its
 * state, except on load, when only a message younger than
 * QUEUE_AUTO_DRAIN_MAX_AGE_MS may run on its own; a row the session held and
 * lists no more was taken or removed. Rows only this tab holds stay.
 */
function reconcileQueue(
  set: SetState,
  conversationId: string,
  entries: LiveSession['queue'],
  loaded: boolean,
): void {
  const now = Date.now();
  const tooOld = (createdAt: number) => now - createdAt > QUEUE_AUTO_DRAIN_MAX_AGE_MS;
  patchMessages(set, conversationId, (messages) => {
    const listed = new Map(entries.map((e) => [e.id, e]));
    const known = new Set(messages.map((m) => m.id));
    const kept = messages.flatMap((m): Message[] => {
      if (!isQueuedRow(m)) return [m];
      const entry = listed.get(m.id);
      if (!entry) {
        return m.queueState === 'sending' || m.queueState === 'held' || m.queueState === 'refused'
          ? [m]
          : [];
      }
      const stale = loaded ? tooOld(entry.createdAt) : m.queueState === 'stale';
      return [{ ...m, queueState: stale ? 'stale' : undefined, queueNote: undefined }];
    });
    const added = entries
      .filter((e) => !known.has(e.id))
      .map(
        (e): Message => ({
          id: e.id,
          role: 'user',
          content: e.text,
          createdAt: e.createdAt,
          status: 'queued',
          queueState: tooOld(e.createdAt) ? 'stale' : undefined,
        }),
      );
    return [...kept, ...added];
  });
}

/** Restored turns as messages, in order after the conversation's `base` time. */
function restoreTurns(sessionId: string, turns: TranscriptTurn[], base: number): Message[] {
  return turns.map(
    (turn, index): Message => ({
      id: `${sessionId}:${String(index)}`,
      role: turn.role,
      content: turn.content,
      // A restored turn carries no timestamp; keep the order stable after createdAt.
      createdAt: base + index,
      status: turn.stopped ? 'stopped' : 'complete',
      ...(turn.turnId !== undefined ? { turnId: turn.turnId } : {}),
      ...(turn.files ? { files: turn.files } : {}),
      ...(turn.blocks ? { blocks: turn.blocks } : {}),
      ...(turn.blockOrder ? { blockOrder: turn.blockOrder } : {}),
    }),
  );
}

function withKeptFilesAndBlocks(restored: Message[], replaced: readonly Message[]): Message[] {
  const key = (m: Message) => `${m.role}:${String(m.turnId)}`;
  const text = (m: Message) => m.content.trim();
  const byTurn = (messages: readonly Message[]) => {
    const turns = new Map<string, Message[]>();
    for (const m of messages) {
      if (m.turnId !== undefined) turns.set(key(m), [...(turns.get(key(m)) ?? []), m]);
    }
    return turns;
  };
  const kept = byTurn(replaced);
  const pairs = new Map<Message, Message>();
  for (const [turn, shown] of byTurn(restored)) {
    const rows = kept.get(turn) ?? [];
    const once = (t: string) =>
      t !== '' &&
      rows.filter((r) => text(r) === t).length === 1 &&
      shown.filter((m) => text(m) === t).length === 1;
    for (const m of shown) {
      const row = once(text(m)) ? rows.find((r) => text(r) === text(m)) : undefined;
      if (row) pairs.set(m, row);
    }
    const unpaired = rows.filter((r) => !once(text(r)));
    const unmatched = shown.filter((m) => !pairs.has(m));
    if (unpaired.length !== unmatched.length) continue;
    for (const [index, m] of unmatched.entries()) {
      const row = unpaired[index];
      if (row) pairs.set(m, row);
    }
  }
  return restored.map((m): Message => {
    const row = pairs.get(m);
    if (!row) return m;
    return {
      ...m,
      ...(m.files === undefined && row.files?.length ? { files: row.files } : {}),
      ...(m.blocks === undefined && row.blocks?.length
        ? { blocks: row.blocks, ...(row.blockOrder ? { blockOrder: row.blockOrder } : {}) }
        : {}),
      ...(row.pendingFiles?.length ? { pendingFiles: row.pendingFiles } : {}),
    };
  });
}

function withTakenFiles(
  restored: Message[],
  replaced: readonly Message[],
  queue: LiveSession['queue'],
): Message[] {
  const listed = new Set(queue.map((e) => e.id));
  const held = newestTurnId(replaced);
  const text = (m: Message) => m.content.trim();
  const taken = replaced.filter(
    (m) =>
      isQueuedRow(m) &&
      (m.queueState === undefined || m.queueState === 'stale') &&
      !listed.has(m.id),
  );
  const newer = restored.filter(
    (m) => m.role === 'user' && m.turnId !== undefined && (held === null || m.turnId > held),
  );
  const carried = new Map<Message, AttachedFile[]>();
  for (const row of taken) {
    if (!row.files?.length) continue;
    const rows = taken.filter((m) => text(m) === text(row));
    const shown = newer.filter((m) => text(m) === text(row));
    const target = rows.length === shown.length ? shown[rows.indexOf(row)] : undefined;
    if (target && target.files === undefined) carried.set(target, row.files);
  }
  return restored.map((m): Message => {
    const files = carried.get(m);
    return files ? { ...m, files } : m;
  });
}

/**
 * Shows the session's open stop card on the thread's last reply, and retires a
 * stop card shown that is no longer open (a later turn closed it).
 */
function placeStopCard(
  set: SetState,
  conversationId: string,
  card: InteractionQuestion | undefined,
): void {
  const openId = card?.questionId;
  patchMessages(set, conversationId, (current) => {
    const messages = current.map(
      (m): Message =>
        m.pendingInteraction?.flow === STOP_CARD_FLOW && m.pendingInteraction.questionId !== openId
          ? { ...m, pendingInteraction: undefined }
          : m,
    );
    if (!card || messages.some((m) => m.pendingInteraction?.questionId === openId)) {
      return messages;
    }
    const shown: InteractionQuestion = card;
    const last = messages.filter((m) => !isQueuedRow(m)).at(-1);
    if (
      last?.role === 'assistant' &&
      last.status !== 'streaming' &&
      !last.pendingInteraction &&
      !last.pendingProposal &&
      !hasOpenConnectCard(last)
    ) {
      const lastId = last.id;
      return messages.map((m) => (m.id === lastId ? { ...m, pendingInteraction: shown } : m));
    }
    return beforeQueued(messages, [
      {
        id: uid('m'),
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        status: 'complete',
        pendingInteraction: shown,
      },
    ]);
  });
}

/**
 * Takes the cards of the messages `holds` picks off the thread. A phrase's mode card shows on
 * the empty reply of the message it holds, which goes with the card, unless the card's answer
 * streams into it. The Connect cards `connect` picks are never taken off: they become records.
 */
function dropCards(
  messages: Message[],
  holds: (m: Message) => boolean,
  connect: (card: ConnectCardRecord) => boolean = () => false,
): Message[] {
  return messages.flatMap((m): Message[] => {
    const kept = recordConnectCards(m, connect);
    if (!holds(kept)) return [kept];
    const emptyModeReply =
      kept.pendingInteraction?.flow === MODE_CARD_FLOW &&
      kept.status !== 'streaming' &&
      !kept.content &&
      !kept.blocks?.length &&
      !kept.connectCards?.length;
    return emptyModeReply
      ? []
      : [{ ...kept, pendingInteraction: undefined, pendingProposal: undefined }];
  });
}

/** A Connect card as its record: it shows its last badge and answers nothing. */
function asRecord(card: ConnectCardRecord): ConnectCardRecord {
  return card.record ? card : { ...card, record: true, pending: undefined };
}

/** The message with the Connect cards `which` picks turned into records. */
function recordConnectCards(m: Message, which: (card: ConnectCardRecord) => boolean): Message {
  if (!m.connectCards?.some((c) => !c.record && which(c))) return m;
  return { ...m, connectCards: m.connectCards.map((c) => (which(c) ? asRecord(c) : c)) };
}

function hasOpenConnectCard(m: Message): boolean {
  return m.connectCards?.some((c) => !c.record) ?? false;
}

/** Cards a stream named resolved, by conversation: one that shows later arrives as a record. */
const resolvedCards = new Map<string, Set<string>>();

/** Takes a card off the thread, wherever it shows: it was answered, or a turn closed it. */
function retireCard(set: SetState, conversationId: string, cardId: string): void {
  const r = runtimes.get(conversationId);
  if (r) {
    if (r.stopCard === cardId) r.stopCard = null;
    if (r.pendingAnswer?.cardId === cardId) r.pendingAnswer = null;
  }
  const resolved = resolvedCards.get(conversationId) ?? new Set<string>();
  resolved.add(cardId);
  resolvedCards.set(conversationId, resolved);
  patchMessages(set, conversationId, (messages) =>
    dropCards(
      messages,
      (m) =>
        m.pendingInteraction?.questionId === cardId || m.pendingProposal?.proposalId === cardId,
      (c) => c.proposal.proposalId === cardId,
    ),
  );
}

/**
 * A card's answer was accepted: the cards shown go, as answering moves the
 * conversation on, except a stop card being answered (`keep`), which goes when
 * its turn names it resolved. The Connect cards become records.
 */
function retireCards(set: SetState, conversationId: string, keep: string | null): void {
  const r = runtimes.get(conversationId);
  if (r && r.stopCard !== keep) r.stopCard = null;
  patchMessages(set, conversationId, (messages) =>
    dropCards(
      messages,
      (m) =>
        (m.pendingInteraction !== undefined || m.pendingProposal !== undefined) &&
        !(keep !== null && m.pendingInteraction?.questionId === keep),
      () => true,
    ),
  );
}

/**
 * A message sent while the stop card, a phrase's mode card, the offer of a
 * deeper pass or a Connect card shows moves on: the card goes here at once (a
 * Connect card becomes a record and stops answering), and the message's turn
 * closes it on the server.
 */
function retireMovedOnCards(set: SetState, get: GetState, conversationId: string): void {
  const stopCard = runtimes.get(conversationId)?.stopCard;
  const messages = get().conversations[conversationId]?.messages ?? [];
  const modeCards = messages.flatMap((m) =>
    m.pendingInteraction?.flow === MODE_CARD_FLOW ? [m.pendingInteraction.questionId] : [],
  );
  const connectCards = messages.flatMap((m) =>
    (m.connectCards ?? []).filter((c) => !c.record).map((c) => c.proposal.proposalId),
  );
  for (const cardId of [...(stopCard ? [stopCard] : []), ...modeCards, ...connectCards]) {
    retireCard(set, conversationId, cardId);
  }
}

function findConnectCard(
  get: GetState,
  conversationId: string,
  proposalId: string,
): ConnectCardRecord | undefined {
  for (const m of get().conversations[conversationId]?.messages ?? []) {
    const card = m.connectCards?.find((c) => c.proposal.proposalId === proposalId);
    if (card) return card;
  }
  return undefined;
}

function patchConnectCard(
  set: SetState,
  conversationId: string,
  proposalId: string,
  patch: (card: ConnectCardRecord) => ConnectCardRecord,
): void {
  patchMessages(set, conversationId, (messages) =>
    messages.map((m) =>
      m.connectCards?.some((c) => c.proposal.proposalId === proposalId)
        ? {
            ...m,
            connectCards: m.connectCards.map((c) =>
              c.proposal.proposalId === proposalId ? patch(c) : c,
            ),
          }
        : m,
    ),
  );
}

/** Every Connect card that is not a record, in any conversation, with its conversation's id. */
export function openConnectCards(): { conversationId: string; card: ConnectCardRecord }[] {
  return Object.values(useConversationsStore.getState().conversations).flatMap((conv) =>
    conv.messages.flatMap((m) =>
      (m.connectCards ?? [])
        .filter((card) => !card.record)
        .map((card) => ({ conversationId: conv.id, card })),
    ),
  );
}

function markInvestorData(set: SetState, conversationId: string, carried: boolean): void {
  set((s) => {
    const c = s.conversations[conversationId];
    if (!c) return s;
    const next = carried || c.investorData === true;
    if (c.investorData === next) return s;
    return {
      conversations: { ...s.conversations, [conversationId]: { ...c, investorData: next } },
    };
  });
}

/** The session became a connected session: it runs the deployment's model from now on. */
function markConnected(set: SetState, conversationId: string): void {
  set((s) => {
    const c = s.conversations[conversationId];
    if (!c || c.connected) return s;
    return { conversations: { ...s.conversations, [conversationId]: { ...c, connected: true } } };
  });
}

/**
 * MESSAGE_TAKEN: the queued messages a turn took show as the user's. At the
 * turn's opening they go before the request's own message (else its reply);
 * mid-turn (an intake) the reply so far closes, dropped if empty, and a new
 * reply follows them.
 */
function takeMessages(
  set: SetState,
  conversationId: string,
  plan: StreamPlan,
  taken: { id: string; text: string }[],
  intake: boolean,
): void {
  const ids = new Set(taken.map((m) => m.id));
  const closing = plan.target;
  const next = intake ? uid('m') : null;
  if (next) plan.target = next;
  patchMessages(set, conversationId, (messages) => {
    const now = Date.now();
    const rows = new Map(messages.filter((m) => ids.has(m.id)).map((m) => [m.id, m]));
    const users = taken.map((m): Message => {
      const row = rows.get(m.id);
      return row
        ? { ...row, status: 'complete', queueState: undefined, queueNote: undefined }
        : { id: m.id, role: 'user', content: m.text, createdAt: now, status: 'complete' };
    });
    const rest = messages.filter((m) => !ids.has(m.id));
    if (next) {
      const at = rest.findIndex((m) => m.id === closing);
      const current = rest[at];
      if (at === -1 || !current) return beforeQueued(rest, [...users, newReply(next)]);
      const closed = withoutPreparing({ ...current, status: 'complete', statusText: undefined });
      const empty =
        !closed.content &&
        !closed.blocks?.length &&
        !closed.pendingInteraction &&
        !closed.pendingProposal &&
        !closed.pendingFiles?.length &&
        !closed.connectCards?.length;
      return [
        ...rest.slice(0, at),
        ...(empty ? [] : [closed]),
        ...users,
        newReply(next),
        ...rest.slice(at + 1),
      ];
    }
    const own = plan.anchor ? rest.findIndex((m) => m.id === plan.anchor) : -1;
    const at = own !== -1 ? own : rest.findIndex((m) => m.id === closing);
    return at === -1
      ? beforeQueued(rest, users)
      : [...rest.slice(0, at), ...users, ...rest.slice(at)];
  });
}

/**
 * A stream this tab read has ended. A card answer that waited goes first; then
 * what is still queued runs as the next turn, unless a stop holds it: for the
 * stop card, or, with no card, for "Send now". A mode card holds it on the
 * orchestrator until its answer, and the rows stay queued. A stream that did not
 * say what is queued leaves it to the poll.
 */
function afterTurn(
  set: SetState,
  get: GetState,
  conversationId: string,
  end: TurnEnd | undefined,
): void {
  const r = rt(conversationId);
  if (r.streams > 0) {
    publish(set, conversationId);
    return;
  }
  r.stopping = false;
  // A held queue is the mode card's when the turn raised one: it is not a stop.
  r.modeHold = end?.queueHeld === true && end.modeCard;
  const queued = end?.queued;
  if (queued === 0) dropServerRows(set, conversationId);
  let waits = false;
  if (r.pendingAnswer) {
    const pending = r.pendingAnswer;
    r.pendingAnswer = null;
    pending.send();
  } else if (queued === undefined) {
    ensurePoll(set, get, conversationId);
  } else if (queued > 0 && end?.queueHeld !== true) {
    void drain(set, get, conversationId);
  } else if (end?.queueHeld === true && !r.stopCard) {
    if (r.modeHold) {
      waitForModeCard(set, conversationId);
    } else {
      waitForSendNow(set, conversationId);
      waits = true;
    }
  }
  // Messages the session would not queue go once its turn has ended, unless a stop
  // that raised no card holds them for "Send now" too.
  if (queuedRows(get, conversationId).some((m) => m.queueState === 'held')) {
    if (get().conversations[conversationId]?.sessionId && getChatService().readSession) {
      ensurePoll(set, get, conversationId);
    } else if (!waits) {
      void sendHeld(set, get, conversationId);
    }
  }
  publish(set, conversationId);
}

/**
 * A stop that raised no card holds the queue: the messages the session holds wait
 * for "Send now", or for the user's next message, whose turn takes them first. The
 * messages it would not queue (`held`) wait for "Send now" too, or go once the next
 * turn ends.
 */
function waitForSendNow(set: SetState, conversationId: string): void {
  patchMessages(set, conversationId, (messages) =>
    messages.map((m): Message => {
      if (!isQueuedRow(m)) return m;
      if (m.queueState === undefined) return { ...m, queueState: 'stale' };
      if (m.queueState === 'held') return { ...m, waitsForSendNow: true };
      return m;
    }),
  );
}

/**
 * A phrase's mode card holds the queue: the orchestrator runs what it holds after the card's
 * answer, so those rows stay queued and none offers "Send now", an old one included.
 */
function waitForModeCard(set: SetState, conversationId: string): void {
  patchMessages(set, conversationId, (messages) =>
    messages.map(
      (m): Message =>
        isQueuedRow(m) && m.queueState === 'stale' ? { ...m, queueState: undefined } : m,
    ),
  );
}

/** The next turn ends a stop's hold: a `held` row then goes once that turn ends. */
function endHold(set: SetState, get: GetState, conversationId: string): void {
  if (!queuedRows(get, conversationId).some((m) => m.waitsForSendNow)) return;
  patchMessages(set, conversationId, (messages) =>
    messages.map((m): Message => (m.waitsForSendNow ? { ...m, waitsForSendNow: undefined } : m)),
  );
}

/**
 * Follows a turn this tab does not read until it ends, then shows it when `reload`; `onTurn`, a
 * wait that began on a compaction, shows it only once a read finds a turn holding the session.
 */
function follow(
  set: SetState,
  get: GetState,
  conversationId: string,
  reload: boolean | 'onTurn',
): void {
  if (!getChatService().readSession) return;
  const r = rt(conversationId);
  r.following = true;
  if (reload === true) r.reloadOnEnd = true;
  if (reload === 'onTurn') r.reloadOnTurn = true;
  ensurePoll(set, get, conversationId);
  publish(set, conversationId);
}

function ensurePoll(set: SetState, get: GetState, conversationId: string): void {
  const r = rt(conversationId);
  if (r.polling || !getChatService().readSession) return;
  r.polling = true;
  const gen = r.pollGen;
  r.pollTimer = setTimeout(() => {
    void pollTick(set, get, conversationId, gen);
  }, POLL_MS);
}

function stopPoll(r: TurnRuntime): void {
  r.pollGen += 1;
  r.polling = false;
  if (r.pollTimer !== null) clearTimeout(r.pollTimer);
  r.pollTimer = null;
}

/** The poll: `GET /sessions/{id}` every POLL_MS until no turn holds the session. */
async function pollTick(
  set: SetState,
  get: GetState,
  conversationId: string,
  gen: number,
): Promise<void> {
  const r = runtimes.get(conversationId);
  if (!r || r.pollGen !== gen) return;
  r.pollTimer = null;
  const sessionId = get().conversations[conversationId]?.sessionId;
  const service = getChatService();
  if (!sessionId || !service.readSession) {
    r.polling = false;
    r.following = false;
    publish(set, conversationId);
    return;
  }
  let live: LiveSession | null;
  try {
    live = await service.readSession(sessionId);
  } catch {
    if (runtimes.get(conversationId) !== r || r.pollGen !== gen) return;
    // A failed read says nothing: read again.
    r.polling = false;
    ensurePoll(set, get, conversationId);
    return;
  }
  if (runtimes.get(conversationId) !== r || r.pollGen !== gen) return;
  r.polling = false;
  if (live?.connected) markConnected(set, conversationId);
  if (!live) {
    // The session is gone: nothing of it runs or waits any more.
    r.following = false;
    r.reloadOnEnd = false;
    r.reloadOnTurn = false;
    r.stopping = false;
    r.stopCard = null;
    r.pendingAnswer = null;
    r.compactingSeen = false;
    settleWaiters(r, false);
    patchMessages(set, conversationId, (messages) => messages.filter((m) => !isQueuedRow(m)));
    publish(set, conversationId);
    return;
  }
  readView(r, live);
  // A wait that began on a compaction meets a turn: it is shown once it ends.
  if (live.turnRunning && r.reloadOnTurn) r.reloadOnEnd = true;
  // A compaction holds the session as a turn does: the poll goes on, and nothing is sent.
  if (live.turnRunning || live.compacting === true) {
    reconcileQueue(set, conversationId, live.queue, false);
    ensurePoll(set, get, conversationId);
    publish(set, conversationId);
    return;
  }
  turnEnded(set, get, conversationId, live, false);
}

/**
 * The session's turn has ended, as the live session shows it (the poll's last
 * read, or a load that found none running). A turn this tab followed is shown
 * from the live session, not the archive. Then, unless a stream of the session
 * is still being read: a card answer that waited, else nothing while a stop card
 * is open, else, while a stop that raised no card holds the queue, what the
 * session holds and the messages it would not queue wait for "Send now", else
 * the messages the session would not queue, else, while a phrase's mode card
 * holds the queue, what the session holds stays queued with no "Send now", else
 * what it holds runs as the next turn. An orchestrator that does not say whether
 * the queue is held reads as not held.
 */
function turnEnded(
  set: SetState,
  get: GetState,
  conversationId: string,
  live: LiveSession,
  loaded: boolean,
): void {
  const r = rt(conversationId);
  stopPoll(r);
  r.following = false;
  // A press that met a held session calls again now.
  settleWaiters(r, true);
  const conv = get().conversations[conversationId];
  if (r.reloadOnEnd && !loaded && conv?.sessionId && viewIsAhead(conv.messages, live)) {
    const { sessionId, createdAt } = conv;
    patchMessages(set, conversationId, (messages) => [
      ...withTakenFiles(
        withKeptFilesAndBlocks(restoreTurns(sessionId, live.turns, createdAt), messages),
        messages,
        live.queue,
      ),
      ...messages.filter(isQueuedRow),
    ]);
  }
  r.reloadOnEnd = false;
  r.reloadOnTurn = false;
  reconcileQueue(set, conversationId, live.queue, loaded);
  r.stopCard = live.stopCard?.questionId ?? null;
  r.modeHold = live.modeCardOpen === true;
  placeStopCard(set, conversationId, live.stopCard);
  if (r.streams > 0) {
    publish(set, conversationId);
    return;
  }
  r.stopping = false;
  const rows = queuedRows(get, conversationId);
  if (r.pendingAnswer) {
    const pending = r.pendingAnswer;
    r.pendingAnswer = null;
    pending.send();
  } else if (r.stopCard) {
    // The queue waits for the stop card's answer.
  } else if (live.queueHeld === true && !r.modeHold) {
    waitForSendNow(set, conversationId);
  } else if (rows.some((m) => m.queueState === 'held')) {
    void sendHeld(set, get, conversationId);
  } else if (r.modeHold) {
    waitForModeCard(set, conversationId);
  } else if (rows.some((m) => m.queueState === undefined)) {
    void drain(set, get, conversationId);
  }
  publish(set, conversationId);
}

/**
 * The server gave this tab a turn, so a turn it followed has ended: the poll
 * stops, and a followed turn this tab did not read is shown from the live
 * session. Only what showed before is replaced; the request's own messages, and
 * the queue, stay after it.
 */
function endFollow(set: SetState, get: GetState, conversationId: string, plan: StreamPlan): void {
  const r = rt(conversationId);
  if (!r.following) return;
  stopPoll(r);
  r.following = false;
  const reload = r.reloadOnEnd;
  r.reloadOnEnd = false;
  r.reloadOnTurn = false;
  publish(set, conversationId);
  const conv = get().conversations[conversationId];
  const service = getChatService();
  if (!reload || !conv?.sessionId || !service.readSession) return;
  const sessionId = conv.sessionId;
  const own = [plan.target, plan.anchor];
  const followed = new Set(
    conv.messages.filter((m) => !isQueuedRow(m) && !own.includes(m.id)).map((m) => m.id),
  );
  void service.readSession(sessionId).then(
    (live) => {
      if (!live) return;
      if (live.connected) markConnected(set, conversationId);
      set((s) => {
        const c = s.conversations[conversationId];
        if (!c || c.sessionId !== sessionId) return s;
        if (!viewIsAhead(c.messages, live)) return s;
        const messages = [
          ...withKeptFilesAndBlocks(
            restoreTurns(sessionId, live.turns, c.createdAt),
            c.messages.filter((m) => followed.has(m.id)),
          ),
          ...c.messages.filter((m) => !followed.has(m.id)),
        ];
        return { conversations: { ...s.conversations, [conversationId]: { ...c, messages } } };
      });
    },
    () => undefined,
  );
}

/**
 * Runs what is queued as the next turn. Never while a stream of the session is
 * read, a turn this tab follows runs, the stop request is in flight (the stop
 * could land on the drain's turn), a stop card is open (the queue waits for its
 * answer) or a phrase's mode card is (the orchestrator runs the queue after its
 * answer). A stop that raised no card is no bar: the turn's end and the session's
 * read never drain while it holds the queue, and "Send now" does.
 */
async function drain(set: SetState, get: GetState, conversationId: string): Promise<void> {
  const r = rt(conversationId);
  const service = getChatService();
  if (!service.drainQueue) return;
  if (r.stopInFlight) {
    r.drainAfterStop = true;
    return;
  }
  if (r.streams > 0 || r.following || isCompacting(r) || r.stopCard || r.modeHold) return;
  const drainFn = service.drainQueue.bind(service);
  const target = uid('m');
  patchMessages(set, conversationId, (messages) => beforeQueued(messages, [newReply(target)]));
  await streamInto(
    set,
    get,
    conversationId,
    {
      target,
      dropIfIdle: true,
      onFailed: (message) => {
        patchMessages(set, conversationId, (messages) =>
          messages.map(
            (m): Message =>
              m.id === target
                ? {
                    ...m,
                    status: 'error',
                    content:
                      message || tr('message.error', 'Something went wrong generating a response.'),
                  }
                : m,
          ),
        );
        waitForSendNow(set, conversationId);
      },
      onAnswer: (answer) => {
        removeMessages(set, conversationId, [target]);
        // A turn holds the session (another tab's): follow it, and drain when it ends. A
        // compaction this tab knows of reloads nothing, unless a turn holds the session after it.
        if (answer.status === 'running') {
          follow(set, get, conversationId, isCompacting(r) ? 'onTurn' : true);
        } else {
          dropServerRows(set, conversationId);
        }
      },
    },
    (opts) => drainFn(opts),
  );
}

/**
 * Messages the session would not queue go once the turn has ended: the first
 * as a turn, the rest queued behind it, in order, each with the files
 * `heldFiles` keeps for it, or, after a reload, the refs its row keeps.
 */
async function sendHeld(set: SetState, get: GetState, conversationId: string): Promise<void> {
  // While a compaction holds the session they wait: the poll's read of its end sends them.
  if (isCompacting(rt(conversationId))) return;
  const held = queuedRows(get, conversationId).filter((m) => m.queueState === 'held');
  for (const row of held) {
    setRow(set, conversationId, row.id, { queueState: 'sending', waitsForSendNow: undefined });
  }
  for (const row of held) {
    const files = heldFiles.get(row.id) ?? row.files ?? [];
    heldFiles.delete(row.id);
    await new Promise<void>((resolve) => {
      void sendQueued(set, get, conversationId, row, files, resolve);
    });
  }
}

/**
 * Sends a message in an idle conversation: the reply streams into the bubble
 * already shown. When a turn holds the session after all (another tab's), the
 * reply goes and the message waits in the queue.
 */
async function sendIdle(
  set: SetState,
  get: GetState,
  conversationId: string,
  userMessage: Message,
  replyId: string,
  files: AttachedFile[],
): Promise<void> {
  await streamInto(
    set,
    get,
    conversationId,
    {
      target: replyId,
      anchor: userMessage.id,
      clientMessageId: userMessage.id,
      onAnswer: (answer) => {
        patchMessages(set, conversationId, (messages) => {
          const rest = messages.filter((m) => m.id !== replyId && m.id !== userMessage.id);
          const row = messages.find((m) => m.id === userMessage.id);
          return row ? [...rest, { ...row, status: 'queued', queueState: 'sending' }] : rest;
        });
        applyQueueAnswer(set, get, conversationId, userMessage.id, answer, files);
      },
    },
    (opts) => getChatService().streamReply(userMessage.content, { ...opts, files }),
  );
}

/**
 * Queues a message behind the running turn: its row waits last in the thread
 * until a turn takes it. When the running turn has ended meanwhile, the message
 * runs as the next turn.
 */
async function sendQueued(
  set: SetState,
  get: GetState,
  conversationId: string,
  row: Message,
  files: AttachedFile[],
  onSettled?: () => void,
): Promise<void> {
  if (!get().conversations[conversationId]?.sessionId || rt(conversationId).preflight) {
    // No session to queue in yet (the first message is still creating it): it
    // goes once that message's turn ends, with its files, which nothing sent yet.
    setRow(set, conversationId, row.id, { queueState: 'held' });
    if (files.length) heldFiles.set(row.id, files);
    onSettled?.();
    return;
  }
  const plan: StreamPlan = {
    target: '',
    clientMessageId: row.id,
    countsWhenStarted: true,
    onSettled,
    onAccepted: () => {
      // The turn ended meanwhile: the message runs as the next turn.
      const replyId = uid('m');
      plan.target = replyId;
      plan.anchor = row.id;
      patchMessages(set, conversationId, (messages) => {
        const sent = messages.find((m) => m.id === row.id);
        if (!sent) return messages;
        return beforeQueued(
          messages.filter((m) => m.id !== row.id),
          [
            { ...sent, status: 'complete', queueState: undefined, queueNote: undefined },
            newReply(replyId),
          ],
        );
      });
    },
    onAnswer: (answer) => {
      applyQueueAnswer(set, get, conversationId, row.id, answer, files);
    },
    onFailed: (message) => {
      setRow(set, conversationId, row.id, {
        queueState: 'refused',
        queueNote:
          message ||
          tr('queue.failed', "Couldn't queue this message. Remove it and send it again."),
      });
    },
  };
  await streamInto(set, get, conversationId, plan, (opts) =>
    getChatService().streamReply(row.content, { ...opts, files, queued: true }),
  );
}

/** How the session answered a message sent with `queue` that ran no turn, on its row. */
function applyQueueAnswer(
  set: SetState,
  get: GetState,
  conversationId: string,
  rowId: string,
  answer: TurnAnswer,
  files: AttachedFile[],
): void {
  const r = rt(conversationId);
  if (answer.status === 'queued') {
    // A MESSAGE_TAKEN may have come first and shown it as sent; then this changes nothing.
    setRow(set, conversationId, rowId, { queueState: undefined, queueNote: undefined });
    // Queued behind a turn this tab does not read: follow it, and run the queue when it ends.
    if (r.streams === 0) follow(set, get, conversationId, true);
  } else if (answer.status === 'refused') {
    // Refused as it arrived: never part of the conversation.
    setRow(set, conversationId, rowId, { queueState: 'refused', queueNote: answer.message });
  } else if (answer.status === 'full') {
    setRow(set, conversationId, rowId, {
      queueState: 'refused',
      queueNote: tr('queue.full', 'The queue is full. Remove a message or wait for the reply.'),
    });
  } else {
    // A turn or an operation holds the session and it was not queued: it goes once the session is
    // free. Met while a compaction runs, it is the compaction, which changes no turn record:
    // nothing reloads, unless a turn holds the session after it.
    if (files.length) heldFiles.set(rowId, files);
    setRow(set, conversationId, rowId, { queueState: 'held' });
    follow(set, get, conversationId, r.streams > 0 ? false : isCompacting(r) ? 'onTurn' : true);
  }
  publish(set, conversationId);
}

/**
 * Answers an open card. The answer is echoed as the user's message, and the
 * continued turn streams into a new reply after it, as a reload shows it; a
 * phrase's mode card (`inPlace`) is answered with no echo, its turn streaming
 * into the empty reply that shows the card. A card answer is never queued: while
 * a reply is being written it waits, and is sent once that turn ends. The card
 * stays until the request is accepted; a stop card stays until its turn names it
 * resolved, since a "Something else" the guardrail refuses leaves it open.
 */
async function answerCard(
  set: SetState,
  get: GetState,
  conversationId: string,
  card: {
    id: string;
    stop: boolean;
    echo?: string;
    echoOf?: Message['echoOf'];
    inPlace?: string;
    modelId?: string;
  },
  makeStream: StreamFactory,
): Promise<void> {
  const r = rt(conversationId);
  if (r.answering === card.id || r.pendingAnswer) return;
  const send = () => {
    void answerCard(set, get, conversationId, card, makeStream);
  };
  if (isBusy(conversationId)) {
    r.pendingAnswer = { cardId: card.id, send };
    if (r.streams === 0) ensurePoll(set, get, conversationId);
    return;
  }

  const now = Date.now();
  const echo: Message | undefined =
    card.inPlace || card.echo === undefined
      ? undefined
      : {
          id: uid('m'),
          role: 'user',
          content: card.echo,
          createdAt: now,
          status: 'complete',
          echoOf: card.echoOf,
        };
  const replyId = card.inPlace ?? uid('m');
  const reply: Message = { ...newReply(replyId), createdAt: now + 1 };
  set((s) => {
    const conv = s.conversations[conversationId];
    if (!conv) return s;
    return {
      conversations: {
        ...s.conversations,
        [conversationId]: {
          ...conv,
          // A Connect card's answer has no echo: its continuation is a new reply.
          messages: card.inPlace
            ? conv.messages.map(
                (m): Message =>
                  m.id === replyId ? { ...m, status: 'streaming', statusText: undefined } : m,
              )
            : beforeQueued(conv.messages, echo ? [echo, reply] : [reply]),
          updatedAt: now,
        },
      },
    };
  });

  r.answering = card.id;
  // Whether the server took the answer, or answered it without a turn.
  const heard = { answered: false };
  await streamInto(
    set,
    get,
    conversationId,
    {
      target: replyId,
      ...(echo ? { anchor: echo.id } : {}),
      ...(card.modelId ? { modelId: card.modelId } : {}),
      // An answer that runs nothing (`keep` on an offer card, or an answer to a
      // card another turn had closed) leaves no echo and no reply.
      dropIfIdle: true,
      onAccepted: () => {
        heard.answered = true;
        retireCards(set, conversationId, card.stop ? card.id : null);
      },
      onAnswer: (answer) => {
        heard.answered = true;
        if (!card.inPlace) {
          removeMessages(set, conversationId, echo ? [echo.id, replyId] : [replyId]);
        } else {
          // The reply goes on showing the card.
          patchMessages(set, conversationId, (messages) =>
            messages.map((m): Message => (m.id === replyId ? { ...m, status: 'complete' } : m)),
          );
        }
        // A Connect card another tab answered or a newer message closed: it is done.
        if (answer.status === 'closed') retireCard(set, conversationId, card.id);
        if (answer.status === 'busy') {
          // Never queued: the card stays, and is answered once the running turn ends.
          rt(conversationId).pendingAnswer = { cardId: card.id, send };
          follow(set, get, conversationId, false);
        }
      },
    },
    makeStream,
  );
  r.answering = null;
  // It failed before any stream: the reply says so, and the card goes (with a
  // stop card's hold on the queue).
  if (!heard.answered) retireCard(set, conversationId, card.id);
}
